
import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { HashRouter, Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  User 
} from 'firebase/auth';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import { 
  getProducts, 
  createOrder, 
  subscribeToOrders, 
  updateOrderStatus, 
  addProduct, 
  deleteProduct,
  createUserProfile,
  getCustomers,
  redeemProduct,
  updateProduct,
  addWallPost,
  likeWallPost,
  subscribeToWallPosts,
  getStoreSettings,
  updateStoreSettings,
  sendMessage,
  subscribeToChat,
  subscribeToAllChats,
  markChatRead,
  updateUserProfile
} from './services/storeService';
import { getBaristaRecommendation } from './services/geminiService';
import { Product, CartItem, Order, UserProfile, ChatMessage, WallPost, StoreSettings, DirectMessage } from './types';

// --- Notifications & Sound System ---

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface AppContextType {
  addToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction: (title: string, message: string, onConfirm: () => void) => void;
}

const AppContext = createContext<AppContextType>({
  addToast: () => {},
  confirmAction: () => {},
});

// --- Utility: Image Compression for Firestore Compatibility (Base64 < 1MB) ---
const compressImage = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        // Max width 1024px is usually safe for Firestore (< 500KB JPEG)
        const MAX_WIDTH = 1024; 
        const scaleSize = MAX_WIDTH / img.width;
        
        if (scaleSize < 1) {
            canvas.width = MAX_WIDTH;
            canvas.height = img.height * scaleSize;
        } else {
            canvas.width = img.width;
            canvas.height = img.height;
        }

        if (ctx) {
            // Fill with white background to handle transparency when converting to JPEG
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            // Compress to JPEG 0.7 quality
            resolve(canvas.toDataURL('image/jpeg', 0.7)); 
        } else {
            reject(new Error("Canvas context failed"));
        }
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const playNotificationSound = (type: 'order' | 'message') => {
  const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContext) return;
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  
  if (type === 'order') {
    // "Ding Dong" sound
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.5);
    gain.gain.setValueAtTime(0.2, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
  } else {
    // Shorter "Pop" sound for messages
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(800, ctx.currentTime);
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
    osc.start();
    osc.stop(ctx.currentTime + 0.1);
  }
};

const ToastContainer = ({ toasts }: { toasts: ToastMessage[] }) => {
  return (
    <div className="fixed top-20 right-4 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map(t => (
        <div key={t.id} className={`pointer-events-auto min-w-[250px] p-4 rounded-lg shadow-lg border-l-4 text-white animate-scale-in flex items-center gap-3 ${
          t.type === 'success' ? 'bg-slate-800 border-green-500' : 
          t.type === 'error' ? 'bg-slate-800 border-red-500' : 
          'bg-slate-800 border-blue-500'
        }`}>
          <i className={`fas ${t.type === 'success' ? 'fa-check-circle text-green-500' : t.type === 'error' ? 'fa-exclamation-circle text-red-500' : 'fa-info-circle text-blue-500'} text-xl`}></i>
          <span className="text-sm font-medium">{t.message}</span>
        </div>
      ))}
    </div>
  );
};

const ConfirmationModal = ({ 
  isOpen, 
  title, 
  message, 
  onConfirm, 
  onCancel 
}: { 
  isOpen: boolean, 
  title: string, 
  message: string, 
  onConfirm: () => void, 
  onCancel: () => void 
}) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 max-w-sm w-full shadow-2xl border border-slate-200 dark:border-slate-700 animate-scale-in transform transition-all">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
        <p className="text-slate-600 dark:text-gray-300 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 rounded text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-700 transition font-medium">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white transition font-medium shadow-md">Confirm</button>
        </div>
      </div>
    </div>
  );
};

// --- Shared Components ---

const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-8">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
  </div>
);

// Custom Full Moon Icon with Craters
const FullMoonIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    aria-hidden="true"
  >
    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 15a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm4-7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm-4-3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
  </svg>
);

// --- Receipt Template Component ---
const ReceiptTemplate = ({ order, settings }: { order: Order, settings: StoreSettings }) => {
  // Calculate Tax details if applicable
  const taxRate = settings.receiptTaxRate || 0;
  const subTotal = taxRate > 0 ? order.total / (1 + (taxRate / 100)) : order.total;
  const taxAmount = order.total - subTotal;

  return (
    <div className="bg-white text-black font-sans p-4 max-w-[320px] mx-auto shadow-sm" style={{ fontFamily: 'Arial, sans-serif' }}>
      <div className="text-center mb-4">
        {settings.logoUrl && (
          <img src={settings.logoUrl} alt="Logo" className="h-16 mx-auto mb-2 grayscale" />
        )}
        <h2 className="text-xl font-bold uppercase tracking-wider">{settings.receiptHeader || "8 Moon Cafe"}</h2>
        {settings.receiptAddress && (
          <p className="text-xs mt-1 text-gray-700 whitespace-pre-line">{settings.receiptAddress}</p>
        )}
        {settings.receiptPhone && (
          <p className="text-xs text-gray-700">Tel. {settings.receiptPhone}</p>
        )}
      </div>

      <div className="border-t border-dashed border-gray-400 py-2 mb-2 text-xs">
        <div className="flex justify-between">
            <span>{new Date(order.createdAt).toLocaleDateString()}</span>
            <span>{new Date(order.createdAt).toLocaleTimeString()}</span>
        </div>
        <div className="flex justify-between mt-1">
            <span>ORDER: #{order.id.slice(-5).toUpperCase()}</span>
            <span>HOST: {order.customerName.split(' ')[0]}</span>
        </div>
      </div>

      <div className="border-t border-dashed border-gray-400 py-2 mb-2 text-xs font-bold flex justify-between">
        <span className="w-8">QTY</span>
        <span className="flex-1 text-left">DESC</span>
        <span className="w-16 text-right">AMT</span>
      </div>

      <div className="border-t border-dashed border-gray-400 py-2 mb-2 text-xs space-y-2">
        {order.items.map((item, idx) => (
          <div key={idx} className="flex justify-between items-start">
            <span className="w-8">{item.quantity}</span>
            <div className="flex-1">
                <div className="uppercase">{item.name}</div>
                {item.note && <div className="text-[10px] italic text-gray-500">({item.note})</div>}
            </div>
            <span className="w-16 text-right">
                {item.price > 0 ? (item.price * item.quantity).toFixed(2) : 'FREE'}
            </span>
          </div>
        ))}
      </div>

      <div className="border-t border-dashed border-gray-400 py-2 text-xs">
         <div className="flex justify-between mb-1">
            <span>SUBTOTAL:</span>
            <span>${subTotal.toFixed(2)}</span>
         </div>
         {taxRate > 0 && (
             <div className="flex justify-between mb-1">
                <span>SALE TAX ({taxRate}%):</span>
                <span>${taxAmount.toFixed(2)}</span>
             </div>
         )}
         <div className="flex justify-between font-bold text-base mt-2">
            <span>AMOUNT:</span>
            <span>${order.total.toFixed(2)}</span>
         </div>
      </div>

      <div className="border-t border-dashed border-gray-400 mt-4 pt-4 text-center">
         <p className="text-xs font-bold mb-2">{settings.receiptFooterMessage || "THANK YOU!"}</p>
         
         {settings.receiptShowBarcode !== false && (
             <div className="mt-2 text-center">
                 <div style={{ fontFamily: '"Libre Barcode 39 Text", cursive', fontSize: '40px' }}>
                    {order.id.slice(-8).toUpperCase()}
                 </div>
             </div>
         )}
      </div>
    </div>
  );
};

// --- Printable Receipt Component ---
const PrintableReceipt = ({ order, settings }: { order: Order, settings: StoreSettings }) => {
  return (
    <div id="printable-receipt" className="hidden">
        <ReceiptTemplate order={order} settings={settings} />
    </div>
  );
};


// --- Desktop Navbar (Visible only on md+ screens) ---
const DesktopNavbar = ({ 
  user, 
  profile, 
  cartCount, 
  onLogout,
  settings,
  onOpenProfile,
  isDarkMode,
  toggleTheme
}: { 
  user: User | null, 
  profile: UserProfile | null, 
  cartCount: number, 
  onLogout: () => void,
  settings: StoreSettings,
  onOpenProfile: () => void,
  isDarkMode: boolean,
  toggleTheme: () => void
}) => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path ? 'text-amber-500 dark:text-amber-400' : 'text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white';
  const { confirmAction } = useContext(AppContext);

  const handleLogoutClick = () => {
    confirmAction("Sign Out", "Are you sure you want to sign out?", onLogout);
  };

  return (
    <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-50 hidden md:block transition-colors duration-300 print:hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center space-x-2">
              {settings.logoUrl ? (
                <img src={settings.logoUrl} alt="Logo" className="h-10 w-10 rounded-full border border-amber-500 object-cover" />
              ) : (
                <i className="fas fa-moon text-amber-500 text-2xl"></i>
              )}
              <span className="font-bold text-xl tracking-wider text-slate-900 dark:text-white font-serif">8 Moon Cafe</span>
            </Link>
            <div className="ml-10 flex items-baseline space-x-4">
              <Link to="/" className={`px-3 py-2 rounded-md text-sm font-medium transition ${isActive('/')}`}>Home</Link>
              <Link to="/menu" className={`px-3 py-2 rounded-md text-sm font-medium transition ${isActive('/menu')}`}>Menu</Link>
              <Link to="/wall" className={`px-3 py-2 rounded-md text-sm font-medium transition ${isActive('/wall')}`}>Moonlit Wall</Link>
              {user && (
                <>
                  <Link to="/orders" className={`px-3 py-2 rounded-md text-sm font-medium transition ${isActive('/orders')}`}>Orders</Link>
                </>
              )}
              {profile?.role === 'owner' && (
                <Link to="/admin" className={`px-3 py-2 rounded-md text-sm font-medium transition ${isActive('/admin')}`}>Admin</Link>
              )}
            </div>
          </div>
          <div>
            <div className="ml-4 flex items-center md:ml-6 space-x-6">
              {/* Theme Toggle */}
              <button 
                onClick={toggleTheme} 
                className="p-2 rounded-full text-slate-500 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 transition focus:outline-none flex items-center justify-center"
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode ? (
                  <FullMoonIcon className="w-6 h-6" />
                ) : (
                  <i className="fas fa-moon text-xl"></i>
                )}
              </button>

              <Link to="/cart" className="relative p-1 text-slate-500 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white focus:outline-none">
                <i className="fas fa-shopping-cart text-xl"></i>
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-amber-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-bounce-short">
                    {cartCount}
                  </span>
                )}
              </Link>
              {user ? (
                <div className="flex items-center space-x-4">
                  <button onClick={onOpenProfile} className="flex items-center gap-2 text-right group focus:outline-none">
                    <div className="text-right">
                      <div className="text-slate-700 dark:text-gray-300 text-sm font-medium group-hover:text-amber-500 dark:group-hover:text-amber-400 transition">{profile?.displayName || user.email?.split('@')[0]}</div>
                      {profile?.points !== undefined && (
                        <div className="text-amber-600 dark:text-amber-500 text-xs font-bold">{profile.points} Moon Pts</div>
                      )}
                    </div>
                    {profile?.photoURL ? (
                       <img src={profile.photoURL} alt="Profile" className="w-9 h-9 rounded-full object-cover border border-slate-300 dark:border-slate-600 group-hover:border-amber-500 transition" />
                    ) : (
                       <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-700 dark:text-white font-bold border border-slate-300 dark:border-slate-600 group-hover:border-amber-500 transition text-sm">
                         {profile?.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                       </div>
                    )}
                  </button>
                  <button onClick={handleLogoutClick} className="text-slate-400 dark:text-gray-400 hover:text-slate-700 dark:hover:text-white text-sm font-medium ml-2" title="Sign Out">
                    <i className="fas fa-sign-out-alt"></i>
                  </button>
                </div>
              ) : (
                <Link to="/login" className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-md text-sm font-medium transition">Login</Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

// ... MobileHeader, MobileBottomNav ...
const MobileHeader = ({ 
  user, 
  profile, 
  settings,
  onOpenProfile,
  isDarkMode,
  toggleTheme
}: { 
  user: User | null, 
  profile: UserProfile | null, 
  settings: StoreSettings,
  onOpenProfile: () => void,
  isDarkMode: boolean,
  toggleTheme: () => void
}) => {
  return (
    <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40 md:hidden px-4 h-16 flex items-center justify-between shadow-lg transition-colors duration-300 print:hidden">
       <div className="flex items-center gap-3">
          {settings.logoUrl ? (
            <img src={settings.logoUrl} alt="Logo" className="h-10 w-10 rounded-full border border-amber-500 object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-amber-500 border border-slate-200 dark:border-slate-700">
               <i className="fas fa-moon text-lg"></i>
            </div>
          )}
          <div className="flex flex-col">
             <span className="font-bold text-slate-900 dark:text-white text-lg leading-none font-serif">8 Moon Cafe</span>
             <span className="text-[10px] text-slate-500 dark:text-gray-400 flex items-center gap-1">
                <i className="fas fa-map-marker-alt text-amber-500"></i> by Agustin Hotspot
             </span>
          </div>
       </div>

       <div className="flex items-center gap-4">
          <button 
            onClick={toggleTheme} 
            className="text-slate-500 dark:text-gray-400 hover:text-amber-500 dark:hover:text-amber-400 transition flex items-center"
          >
             {isDarkMode ? (
                <FullMoonIcon className="w-6 h-6" />
              ) : (
                <i className="fas fa-moon text-lg"></i>
              )}
          </button>
          
          {user ? (
              <button onClick={onOpenProfile} className="relative">
                {profile?.photoURL ? (
                    <img src={profile.photoURL} alt="User" className="w-10 h-10 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700" />
                ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-700 dark:text-white font-bold border border-slate-300 dark:border-slate-600">
                      {profile?.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                    </div>
                )}
                <div className="absolute -bottom-1 -right-1 bg-amber-600 text-[10px] font-bold text-white px-1.5 rounded-full border border-white dark:border-slate-900">
                    {profile?.points}
                </div>
              </button>
          ) : (
              <Link to="/login" className="text-sm font-bold text-amber-500">Login</Link>
          )}
       </div>
    </div>
  );
};

const MobileBottomNav = ({ 
  cartCount,
  user,
  role
}: { 
  cartCount: number,
  user: User | null,
  role?: string
}) => {
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path ? 'text-amber-600 dark:text-amber-500' : 'text-slate-400 dark:text-gray-400';

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 h-20 md:hidden z-50 flex items-center justify-around pb-2 shadow-[0_-5px_15px_rgba(0,0,0,0.1)] dark:shadow-[0_-5px_15px_rgba(0,0,0,0.3)] transition-colors duration-300 print:hidden">
       <Link to="/" className={`flex flex-col items-center gap-1 p-2 ${isActive('/')}`}>
          <i className="fas fa-home text-xl"></i>
          <span className="text-[10px]">Home</span>
       </Link>
       
       <Link to="/menu" className={`flex flex-col items-center gap-1 p-2 ${isActive('/menu')}`}>
          <i className="fas fa-coffee text-xl"></i>
          <span className="text-[10px]">Menu</span>
       </Link>

       {/* Prominent Center Button (Cart) */}
       <div className="relative -top-5">
         <Link to="/cart" className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg border-4 border-white dark:border-slate-900 ${location.pathname === '/cart' ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 dark:bg-slate-700 text-white'}`}>
            <i className="fas fa-shopping-basket text-xl"></i>
            {cartCount > 0 && (
               <span className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border border-white dark:border-slate-900 animate-bounce-short">
                  {cartCount}
               </span>
            )}
         </Link>
       </div>

       <Link to="/wall" className={`flex flex-col items-center gap-1 p-2 ${isActive('/wall')}`}>
          <i className="fas fa-camera-retro text-xl"></i>
          <span className="text-[10px]">Wall</span>
       </Link>

       {user ? (
          <Link to={role === 'owner' ? "/admin" : "/orders"} className={`flex flex-col items-center gap-1 p-2 ${isActive(role === 'owner' ? "/admin" : "/orders")}`}>
             <i className={`fas ${role === 'owner' ? 'fa-chart-pie' : 'fa-receipt'} text-xl`}></i>
             <span className="text-[10px]">{role === 'owner' ? 'Admin' : 'Orders'}</span>
          </Link>
       ) : (
          <Link to="/login" className={`flex flex-col items-center gap-1 p-2 ${isActive('/login')}`}>
             <i className="fas fa-user text-xl"></i>
             <span className="text-[10px]">Login</span>
          </Link>
       )}
    </div>
  );
};

const FloatingChat = ({ user, profile, products }: { user: User | null, profile: UserProfile | null, products: Product[] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'model', text: 'Greetings! I am Moon Bot 🌑. Tell me how you are feeling or what flavors you like, and I shall gaze into the stars for a recommendation.' }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  const [chatMode, setChatMode] = useState<'bot' | 'owner'>('bot');
  const [directChat, setDirectChat] = useState<DirectMessage | null>(null);
  
  const { addToast } = useContext(AppContext);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages, directChat, isOpen]);

  useEffect(() => {
    if (chatMode === 'owner' && user && isOpen) {
      const unsub = subscribeToChat(user.uid, (chatData) => {
        setDirectChat(chatData);
      });
      return () => unsub();
    }
  }, [chatMode, user, isOpen]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    const userMsg = input;
    setInput('');

    if (chatMode === 'bot') {
      setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
      setIsTyping(true);
      const reply = await getBaristaRecommendation(userMsg, products);
      setMessages(prev => [...prev, { role: 'model', text: reply }]);
      setIsTyping(false);
    } else {
      if (!user) {
        addToast("Please login to chat with the owner.", "error");
        return;
      }
      try {
        await sendMessage(user.uid, profile?.displayName || user.email?.split('@')[0] || 'Customer', {
            senderId: user.uid,
            senderName: profile?.displayName || 'Customer',
            role: 'customer',
            text: userMsg,
            timestamp: Date.now()
        });
      } catch (err) {
        addToast("Failed to send message.", "error");
      }
    }
  };

  return (
    <>
      {!isOpen && (
        <button 
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-40 w-14 h-14 md:w-16 md:h-16 bg-amber-600 hover:bg-amber-500 rounded-full shadow-lg shadow-amber-900/50 flex items-center justify-center text-white text-2xl transition-transform hover:scale-110 border-2 border-amber-400 print:hidden animate-scale-in"
          title="Chat with Moon Bot or Owner"
        >
          <i className="fas fa-comment-dots"></i>
        </button>
      )}

      {isOpen && (
        <div className="fixed bottom-24 right-4 md:bottom-6 md:right-6 z-50 w-[90vw] md:w-96 h-[80vh] md:h-[500px] flex flex-col bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-fade-in-up print:hidden">
           <div className="bg-slate-100 dark:bg-slate-900 p-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
             <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${chatMode === 'bot' ? 'bg-indigo-100 dark:bg-indigo-900 border-indigo-500' : 'bg-amber-100 dark:bg-amber-900 border-amber-500'}`}>
                  <i className={`fas ${chatMode === 'bot' ? 'fa-robot text-indigo-500 dark:text-indigo-300' : 'fa-user-tie text-amber-600 dark:text-amber-300'}`}></i>
                </div>
                <div>
                  <h3 className="text-slate-900 dark:text-white font-bold text-sm">{chatMode === 'bot' ? 'Moon Bot' : 'Store Owner'}</h3>
                  <p className="text-slate-500 dark:text-gray-400 text-[10px]">{chatMode === 'bot' ? 'AI Barista' : 'Direct Message'}</p>
                </div>
             </div>
             <div className="flex items-center gap-2">
                 <button 
                   onClick={() => setChatMode(prev => prev === 'bot' ? 'owner' : 'bot')}
                   className="text-[10px] bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 text-slate-700 dark:text-white px-2 py-1 rounded transition border border-slate-300 dark:border-slate-600"
                 >
                   Switch to {chatMode === 'bot' ? 'Owner' : 'AI'}
                 </button>
                 <button 
                   onClick={() => setIsOpen(false)}
                   className="text-slate-400 dark:text-gray-400 hover:text-slate-600 dark:hover:text-white px-2"
                 >
                   <i className="fas fa-times"></i>
                 </button>
             </div>
           </div>
           
           <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/95 dark:bg-slate-800/95 scroll-smooth min-h-0">
             {chatMode === 'bot' ? (
               <>
                  {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                        msg.role === 'user' 
                          ? 'bg-amber-600 text-white rounded-br-none' 
                          : 'bg-white dark:bg-slate-700 text-slate-800 dark:text-gray-200 border border-slate-200 dark:border-slate-600 rounded-bl-none shadow-sm'
                      }`}>
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-slate-700 text-slate-500 dark:text-gray-200 rounded-lg rounded-bl-none px-3 py-2 text-xs animate-pulse border border-slate-200 dark:border-slate-600">
                        Consulting the stars...
                      </div>
                    </div>
                  )}
               </>
             ) : (
               <>
                 {!user ? (
                   <div className="text-center text-gray-500 dark:text-gray-400 mt-10 text-sm">Please login to chat with the owner.</div>
                 ) : (
                   <>
                      {directChat?.messages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.senderId === user.uid ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                            msg.senderId === user.uid 
                              ? 'bg-amber-600 text-white rounded-br-none' 
                              : 'bg-white dark:bg-slate-700 text-slate-800 dark:text-gray-200 border border-slate-200 dark:border-slate-600 rounded-bl-none shadow-sm'
                          }`}>
                            {msg.text}
                          </div>
                        </div>
                      ))}
                      {(!directChat || directChat.messages.length === 0) && (
                        <div className="text-center text-gray-500 dark:text-gray-500 text-xs italic mt-10">
                          Start a conversation with the store owner...
                        </div>
                      )}
                   </>
                 )}
               </>
             )}
             <div ref={chatEndRef} />
           </div>

           <div className="p-3 bg-slate-100 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
             <div className="flex gap-2">
               <input 
                 type="text" 
                 value={input}
                 onChange={(e) => setInput(e.target.value)}
                 onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                 placeholder={chatMode === 'bot' ? "Suggest a drink..." : "Message owner..."}
                 className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white text-sm focus:outline-none focus:border-amber-500"
                 disabled={chatMode === 'owner' && !user}
               />
               <button 
                 onClick={handleSendMessage}
                 disabled={(isTyping) || (chatMode === 'owner' && !user)}
                 className="bg-amber-600 hover:bg-amber-500 text-white p-2 rounded-md transition disabled:opacity-50"
               >
                 <i className="fas fa-paper-plane"></i>
               </button>
             </div>
           </div>
        </div>
      )}
    </>
  );
};

// ... MoonlitWall, AdminDashboard ...

const MoonlitWall = ({ user, profile }: { user: User | null, profile: UserProfile | null }) => {
    const [posts, setPosts] = useState<WallPost[]>([]);
    const [isPosting, setIsPosting] = useState(false);
    const [newPost, setNewPost] = useState({ title: '', caption: '', imageUrl: '' });
    const [selectedPost, setSelectedPost] = useState<WallPost | null>(null);
    const { addToast, confirmAction } = useContext(AppContext);
  
    useEffect(() => {
      const unsub = subscribeToWallPosts(setPosts);
      return () => unsub();
    }, []);
  
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        try {
          const compressed = await compressImage(file);
          setNewPost(prev => ({ ...prev, imageUrl: compressed }));
        } catch (err) {
          addToast("Error processing image. Try another.", "error");
        }
      }
    };
  
    const attemptPostSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      confirmAction("Share Post", "Are you sure you want to share this on the Moonlit Wall?", handlePostSubmit);
    };
  
    const handlePostSubmit = async () => {
      if (!user || !profile || !newPost.imageUrl) {
          addToast("An image is required!", "error");
          return;
      }
  
      try {
        await addWallPost({
          userId: user.uid,
          userRole: profile.role,
          authorName: profile.displayName || user.email?.split('@')[0] || 'Anonymous',
          authorPhotoURL: profile.photoURL,
          title: newPost.title || 'Untitled Moment',
          caption: newPost.caption || '',
          imageUrl: newPost.imageUrl,
          createdAt: Date.now()
        });
    
        setNewPost({ title: '', caption: '', imageUrl: '' });
        setIsPosting(false);
        addToast("Shared successfully!", "success");
      } catch (err) {
        console.error("Post error:", err);
        addToast("Failed to share post.", "error");
      }
    };
  
    const handleLike = async (post: WallPost) => {
      if (!user) {
        addToast("Login to like posts!", "error");
        return;
      }
      const isLiked = post.likedBy?.includes(user.uid);
      await likeWallPost(post.id, user.uid, isLiked);
    };
  
    const handleShare = async (post: WallPost) => {
      if (navigator.share) {
        try {
          await navigator.share({
            title: post.title,
            text: `Check out this moment from 8 Moon Cafe: ${post.title}`,
            url: window.location.href 
          });
        } catch (err) {
          console.error("Share failed", err);
        }
      } else {
        addToast("Link copied to clipboard!", "success");
        navigator.clipboard.writeText(window.location.href);
      }
    };
  
    return (
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Moonlit Wall</h2>
            <p className="text-slate-500 dark:text-gray-400 mt-1">Moments captured under the 8 moons.</p>
          </div>
          <div className="flex gap-4">
            {user && !isPosting && (
              <button 
                onClick={() => setIsPosting(true)}
                className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded-md font-medium transition shadow-lg flex items-center gap-2"
              >
                <i className="fas fa-camera"></i> Share
              </button>
            )}
          </div>
        </div>
  
        {isPosting && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 w-full max-w-lg shadow-2xl animate-scale-in">
                  <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white">Create New Post</h3>
                      <button onClick={() => setIsPosting(false)} className="text-slate-400 hover:text-slate-600 dark:text-gray-400 dark:hover:text-white"><i className="fas fa-times"></i></button>
                  </div>
                  <form onSubmit={attemptPostSubmit} className="space-y-4">
                      <div>
                          <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Photo (Required)</label>
                          <input 
                              type="file" 
                              accept="image/*" 
                              onChange={handleImageChange} 
                              required
                              className="w-full text-sm text-slate-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-slate-200 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-white hover:file:bg-slate-300 dark:hover:file:bg-slate-600"
                          />
                      </div>
                      {newPost.imageUrl && (
                          <div className="relative h-48 w-full rounded border border-slate-300 dark:border-slate-600 overflow-hidden">
                              <img src={newPost.imageUrl} alt="Preview" className="w-full h-full object-contain bg-black" />
                          </div>
                      )}
                      <div>
                          <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Title (Optional)</label>
                          <input 
                              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" 
                              value={newPost.title}
                              onChange={e => setNewPost({...newPost, title: e.target.value})}
                              placeholder="Give your moment a title"
                          />
                      </div>
                      <div>
                          <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Caption (Optional)</label>
                          <textarea 
                              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" 
                              rows={3}
                              value={newPost.caption}
                              onChange={e => setNewPost({...newPost, caption: e.target.value})}
                              placeholder="Describe your experience..."
                          />
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                          <button type="submit" disabled={!newPost.imageUrl} className="flex-1 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-2 rounded font-medium shadow-md">Share Post</button>
                          <button type="button" onClick={() => setIsPosting(false)} className="flex-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white py-2 rounded font-medium">Cancel</button>
                      </div>
                  </form>
              </div>
          </div>
        )}
  
        {/* Unified Gallery (Mixed Owner & Customer Posts) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-fade-in-up">
             {posts.map(post => (
               <div key={post.id} className="bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 shadow-lg cursor-pointer hover:shadow-xl transition group flex flex-col" onClick={() => setSelectedPost(post)}>
                  <div className="h-64 overflow-hidden relative">
                     <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                     {/* Badge for Owner posts */}
                     {post.userRole === 'owner' && (
                        <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm z-10 border border-white/20">
                            OFFICIAL
                        </div>
                     )}
                     <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent flex justify-between items-center text-white">
                        <span className="text-xs font-bold flex items-center gap-1">
                          {post.authorPhotoURL ? (
                             <img src={post.authorPhotoURL} className="w-5 h-5 rounded-full border border-white" alt="" />
                          ) : (
                             <i className="fas fa-user-circle text-sm"></i>
                          )}
                          <span className="truncate max-w-[120px]">{post.authorName}</span>
                        </span>
                     </div>
                  </div>
                  <div className="p-3 flex-1 flex flex-col">
                     <div className="flex justify-between items-center mb-1">
                        <h3 className="font-bold text-slate-900 dark:text-white truncate text-sm flex-1 mr-2">{post.title}</h3>
                        <span className="text-xs text-slate-500 flex items-center gap-1 flex-shrink-0"><i className="fas fa-heart text-red-500"></i> {post.likes || 0}</span>
                     </div>
                     <p className="text-xs text-slate-500 dark:text-gray-400 line-clamp-2">{post.caption}</p>
                  </div>
               </div>
             ))}
             {posts.length === 0 && (
               <div className="col-span-full text-center py-20 text-gray-500">No moments shared yet. Be the first!</div>
             )}
        </div>
  
        {/* Instagram Style Modal */}
        {selectedPost && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fade-in-up" onClick={() => setSelectedPost(null)}>
             <div className="bg-white dark:bg-slate-900 max-w-4xl w-full max-h-[90vh] flex flex-col md:flex-row rounded-xl overflow-hidden shadow-2xl animate-scale-in" onClick={e => e.stopPropagation()}>
                {/* Image Side */}
                <div className="md:w-3/5 bg-black flex items-center justify-center">
                   <img src={selectedPost.imageUrl} className="max-w-full max-h-[50vh] md:max-h-[90vh] object-contain" alt={selectedPost.title} />
                </div>
                
                {/* Details Side */}
                <div className="md:w-2/5 flex flex-col border-l border-slate-200 dark:border-slate-800">
                   {/* Header */}
                   <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                         {selectedPost.authorPhotoURL ? (
                            <img src={selectedPost.authorPhotoURL} className="w-8 h-8 rounded-full object-cover" alt="" />
                         ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-gray-300">
                               {selectedPost.authorName[0]}
                            </div>
                         )}
                         <div>
                            <span className="font-bold text-sm text-slate-900 dark:text-white block">{selectedPost.authorName}</span>
                            {selectedPost.userRole === 'owner' && <span className="text-[10px] bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 px-1.5 rounded font-bold">Official</span>}
                         </div>
                      </div>
                      <button onClick={() => setSelectedPost(null)} className="text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white"><i className="fas fa-times"></i></button>
                   </div>
  
                   {/* Comments/Caption Area */}
                   <div className="flex-1 p-4 overflow-y-auto">
                      <div className="mb-4">
                         <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-2">{selectedPost.title}</h3>
                         <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{selectedPost.caption}</p>
                         <p className="text-xs text-slate-400 mt-2 uppercase">{new Date(selectedPost.createdAt).toLocaleDateString()}</p>
                      </div>
                   </div>
  
                   {/* Actions Footer */}
                   <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                      <div className="flex justify-between items-center mb-2">
                         <div className="flex gap-4">
                            <button 
                              onClick={() => handleLike(selectedPost)} 
                              className={`text-2xl transition ${selectedPost.likedBy?.includes(user?.uid || '') ? 'text-red-500 animate-bounce-short' : 'text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300'}`}
                            >
                               <i className={`${selectedPost.likedBy?.includes(user?.uid || '') ? 'fas' : 'far'} fa-heart`}></i>
                            </button>
                            <button 
                               onClick={() => handleShare(selectedPost)}
                               className="text-2xl text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300"
                            >
                               <i className="fas fa-paper-plane"></i>
                            </button>
                         </div>
                         
                         {/* Facebook Share Button */}
                         <a 
                           href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent("Check out this post on 8 Moon Cafe!")}`} 
                           target="_blank" 
                           rel="noreferrer"
                           className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                         >
                           <i className="fab fa-facebook"></i> Share
                         </a>
                      </div>
                      <div className="font-bold text-sm text-slate-900 dark:text-white">
                         {selectedPost.likes || 0} likes
                      </div>
                   </div>
                </div>
             </div>
          </div>
        )}
      </div>
    );
  };
  

const AdminDashboard = ({ profile }: { profile: UserProfile }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [customers, setCustomers] = useState<UserProfile[]>([]);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '', description: '', price: 0, category: 'coffee', imageUrl: '', available: true, pointsEarned: 0, additionalImages: [], isRedeemable: false, pointCost: 0
  });
  // Default to 'orders' (Live Queue)
  const [activeTab, setActiveTab] = useState<'orders' | 'history' | 'products' | 'customers' | 'settings' | 'messages' | 'sales' | 'receipt'>('orders');
  const [settings, setSettings] = useState<StoreSettings>({});
  
  // Printing
  const [orderToPrint, setOrderToPrint] = useState<Order | null>(null);
  
  // Messaging
  const [allChats, setAllChats] = useState<DirectMessage[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [activeChatMessages, setActiveChatMessages] = useState<DirectMessage | null>(null);
  const [adminReply, setAdminReply] = useState('');
  const chatScrollRef = useRef<HTMLDivElement>(null);

  // Mobile Order List View State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Analytics Date Filter
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);

  // Sales Stats
  const [stats, setStats] = useState({
    totalRevenue: 0,
    todayRevenue: 0,
    monthRevenue: 0,
    totalOrders: 0,
    topItems: [] as {name: string, count: number}[],
    selectedDateRevenue: 0,
    selectedDateOrders: 0,
    graphData: [] as { label: string; fullDate: string; value: number }[]
  });

  const { addToast, confirmAction } = useContext(AppContext);
  const prevOrdersLength = useRef(0);
  const prevUnreadMessages = useRef(0);

  useEffect(() => {
    const unsub = subscribeToOrders((newOrders) => {
        // Notification Logic for New Orders
        const newLiveOrdersCount = newOrders.filter(o => ['pending'].includes(o.status)).length;
        
        // Use prevOrdersLength to detect changes. 
        if (newOrders.length > prevOrdersLength.current && prevOrdersLength.current !== 0) {
           playNotificationSound('order');
           addToast("New Order Received!", "success");
        }
        prevOrdersLength.current = newOrders.length;
        setOrders(newOrders);
    });
    getProducts().then(setProducts);
    getCustomers().then(setCustomers);
    getStoreSettings().then(setSettings);
    
    const unsubChats = subscribeToAllChats((chats) => {
        const unreadCount = chats.filter(c => c.unreadByOwner).length;
        if (unreadCount > prevUnreadMessages.current && prevUnreadMessages.current !== -1) {
            playNotificationSound('message');
            addToast("New Message!", "info");
        }
        prevUnreadMessages.current = unreadCount;
        setAllChats(chats);
    });

    return () => {
      unsub();
      unsubChats();
    };
  }, []);

  useEffect(() => {
    if (orders.length === 0) return;
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const validOrders = orders.filter(o => o.status === 'completed');
    const totalRev = validOrders.reduce((acc, o) => acc + (o.isRedemption ? 0 : o.total), 0);
    const todayRev = validOrders.filter(o => o.createdAt >= todayStart).reduce((acc, o) => acc + (o.isRedemption ? 0 : o.total), 0);
    const monthRev = validOrders.filter(o => o.createdAt >= monthStart).reduce((acc, o) => acc + (o.isRedemption ? 0 : o.total), 0);
    
    // Top Items Logic
    const itemCounts: Record<string, number> = {};
    validOrders.forEach(o => {
      o.items.forEach(i => {
        itemCounts[i.name] = (itemCounts[i.name] || 0) + i.quantity;
      });
    });
    const topItemsSorted = Object.entries(itemCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Selected Date Stats
    let selectedDateRev = 0;
    let selectedDateCount = 0;
    if (selectedDate) {
        const selStart = new Date(selectedDate).setHours(0,0,0,0);
        const selEnd = new Date(selectedDate).setHours(23,59,59,999);
        const selOrders = validOrders.filter(o => o.createdAt >= selStart && o.createdAt <= selEnd);
        selectedDateRev = selOrders.reduce((acc, o) => acc + (o.isRedemption ? 0 : o.total), 0);
        selectedDateCount = selOrders.length;
    }

    // Graph Data (Last 7 Days)
    const graphData = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const dayStart = new Date(d.setHours(0,0,0,0)).getTime();
        const dayEnd = new Date(d.setHours(23,59,59,999)).getTime();
        
        const dayOrders = validOrders.filter(o => o.createdAt >= dayStart && o.createdAt <= dayEnd);
        const dayTotal = dayOrders.reduce((acc, o) => acc + (o.isRedemption ? 0 : o.total), 0);
        
        graphData.push({
            label: new Date(dayStart).toLocaleDateString('en-US', { weekday: 'short' }),
            fullDate: new Date(dayStart).toLocaleDateString(),
            value: dayTotal
        });
    }

    setStats({
      totalRevenue: totalRev,
      todayRevenue: todayRev,
      monthRevenue: monthRev,
      totalOrders: validOrders.length,
      topItems: topItemsSorted,
      selectedDateRevenue: selectedDateRev,
      selectedDateOrders: selectedDateCount,
      graphData: graphData
    });
  }, [orders, selectedDate]);

  useEffect(() => {
    if (selectedChatId) {
       const chat = allChats.find(c => c.id === selectedChatId);
       setActiveChatMessages(chat || null);
       if (chat && chat.unreadByOwner) {
         markChatRead(chat.id, 'owner');
       }
    }
  }, [allChats, selectedChatId]);

  useEffect(() => {
    chatScrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChatMessages]);

  const handleStatusUpdate = async (orderId: string, status: Order['status']) => {
    await updateOrderStatus(orderId, status);
    addToast(`Order updated to ${status}`, "success");
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>, isMain: boolean = true) => {
     const file = e.target.files?.[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        if (isMain) {
          setNewProduct(prev => ({ ...prev, imageUrl: compressed }));
        } else {
          setNewProduct(prev => ({ 
            ...prev, 
            additionalImages: [...(prev.additionalImages || []), compressed] 
          }));
        }
      } catch(err) {
        addToast("Error processing image. Try another.", "error");
      }
    }
  };

  const handleToggleAvailability = async (product: Product) => {
    const updated = !product.available;
    await updateProduct(product.id, { available: updated });
    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, available: updated } : p));
  };

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    confirmAction("Add Product", "Add this new product to the menu?", async () => {
        if (!newProduct.name || (newProduct.price === undefined && !newProduct.isRedeemable)) return;
        const finalPoints = newProduct.pointsEarned ? Number(newProduct.pointsEarned) : Math.floor(Number(newProduct.price || 0));
        await addProduct({
          ...newProduct as any,
          price: Number(newProduct.price || 0),
          pointsEarned: finalPoints,
          imageUrl: newProduct.imageUrl || `https://picsum.photos/400/400?random=${Date.now()}`,
          isRedeemable: !!newProduct.isRedeemable,
          pointCost: Number(newProduct.pointCost || 0)
        });
        setNewProduct({ name: '', description: '', price: 0, category: 'coffee', imageUrl: '', available: true, pointsEarned: 0, additionalImages: [], isRedeemable: false, pointCost: 0 });
        getProducts().then(setProducts); 
        addToast("Product added!", "success");
    });
  };

  const handleDeleteProduct = async (id: string) => {
    confirmAction("Delete Product", "Are you sure? This cannot be undone.", async () => {
        await deleteProduct(id);
        getProducts().then(setProducts);
        addToast("Product deleted.", "info");
    });
  };

  const handleSettingsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    confirmAction("Update Settings", "Save changes to store settings?", async () => {
        try {
            await updateStoreSettings(settings);
            addToast("Settings updated!", "success");
            setTimeout(() => window.location.reload(), 1000);
        } catch (err) {
            console.error(err);
            addToast("Failed to update settings.", "error");
        }
    });
  };

  const handleSettingImage = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'cover') => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        if (type === 'logo') setSettings(prev => ({ ...prev, logoUrl: compressed }));
        else setSettings(prev => ({ ...prev, coverPhotoUrl: compressed }));
      } catch(err) {
         addToast("Error processing image. Please try another.", "error");
      }
    }
  };

  const handleSendAdminReply = async () => {
    if (!selectedChatId || !adminReply.trim()) return;
    try {
        await sendMessage(selectedChatId, activeChatMessages?.customerName || 'Customer', {
            senderId: profile.uid,
            senderName: profile.displayName || 'Owner',
            role: 'owner',
            text: adminReply,
            timestamp: Date.now()
        });
        setAdminReply('');
    } catch (err) {
        addToast("Failed to send message", "error");
    }
  };

  const handlePrintOrder = (order: Order) => {
    setOrderToPrint(order);
    setTimeout(() => {
      window.print();
      setOrderToPrint(null);
    }, 500);
  };

  if (profile.role !== 'owner') return <Navigate to="/" />;

  const liveOrders = orders.filter(o => ['pending', 'preparing', 'ready'].includes(o.status));
  const pastOrders = orders.filter(o => ['completed', 'cancelled'].includes(o.status));

  // Admin Tab Configuration
  const adminTabs = [
    { id: 'orders', label: 'Live Orders', icon: 'fa-clipboard-list', count: liveOrders.length },
    { id: 'history', label: 'History', icon: 'fa-history' },
    { id: 'sales', label: 'Analytics', icon: 'fa-chart-line' },
    { id: 'products', label: 'Products', icon: 'fa-box-open' },
    { id: 'customers', label: 'Customers', icon: 'fa-users' },
    { id: 'messages', label: 'Messages', icon: 'fa-comments', badge: allChats.filter(c => c.unreadByOwner).length },
    { id: 'receipt', label: 'Receipt', icon: 'fa-print' }, // New Tab
    { id: 'settings', label: 'Settings', icon: 'fa-cog' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-10">
      {orderToPrint && <PrintableReceipt order={orderToPrint} settings={settings} />}

      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 print:hidden">
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">Owner Dashboard</h2>
        
        {/* Desktop Navigation (Tabs) */}
        <div className="hidden md:flex bg-white dark:bg-slate-800 rounded-lg p-1 shadow-sm border border-slate-200 dark:border-slate-700 overflow-x-auto max-w-full">
             {adminTabs.map(tab => (
                 <button 
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)} 
                    className={`px-4 py-2 rounded-md transition whitespace-nowrap text-sm font-bold flex items-center gap-2 ${activeTab === tab.id ? 'bg-amber-500 text-slate-900 shadow' : 'text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white'}`}
                 >
                    <i className={`fas ${tab.icon}`}></i>
                    {tab.label}
                    {(tab.count || 0) > 0 && <span className="bg-white/20 px-1.5 rounded-full text-xs">{tab.count}</span>}
                    {(tab.badge || 0) > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-1.5">{tab.badge}</span>}
                 </button>
             ))}
        </div>
      </div>

      {/* Mobile Navigation (Shopee Style - Horizontal Icon Scroll) */}
      <div className="md:hidden overflow-x-auto no-scrollbar pb-2 mb-4 -mx-4 px-4">
        <div className="flex space-x-4 min-w-max">
            {adminTabs.map(tab => (
                <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`flex flex-col items-center justify-center min-w-[72px] p-2 rounded-lg transition ${activeTab === tab.id ? 'text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/20' : 'text-slate-500 dark:text-gray-400'}`}
                >
                    <div className="relative">
                        <i className={`fas ${tab.icon} text-xl mb-1`}></i>
                        {(tab.badge || 0) > 0 && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white dark:border-slate-900">
                                {tab.badge}
                            </span>
                        )}
                        {(tab.count || 0) > 0 && (
                            <span className="absolute -top-2 -right-2 bg-amber-600 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white dark:border-slate-900">
                                {tab.count}
                            </span>
                        )}
                    </div>
                    <span className="text-[10px] font-bold">{tab.label}</span>
                </button>
            ))}
        </div>
      </div>

      {activeTab === 'sales' && (
        <div className="space-y-6 md:space-y-8 animate-fade-in-up print:hidden">
            {/* ... Sales Tab Content ... */}
            <div className="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-md">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white">Performance Overview</h3>
                    <div className="w-full md:w-auto">
                        <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 mb-1 md:hidden">Select Date</label>
                        <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-900 p-2 rounded-lg w-full md:w-auto">
                            <span className="text-sm font-bold text-slate-600 dark:text-gray-400 hidden md:inline">Review Date:</span>
                            <i className="fas fa-calendar text-slate-400 md:hidden"></i>
                            <input 
                                type="date" 
                                value={selectedDate} 
                                onChange={(e) => setSelectedDate(e.target.value)}
                                className="bg-transparent border-none text-slate-900 dark:text-white text-sm outline-none focus:ring-0 w-full"
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    <div className="bg-amber-50 dark:bg-slate-900/50 p-4 rounded-lg border border-amber-100 dark:border-slate-700 relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-4 opacity-10">
                            <i className="fas fa-coins text-6xl text-amber-600"></i>
                        </div>
                        <div className="text-amber-600 dark:text-amber-500 font-bold uppercase text-xs tracking-wider mb-1">Revenue ({new Date(selectedDate).toLocaleDateString()})</div>
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">₱{stats.selectedDateRevenue.toLocaleString()}</div>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="text-slate-500 dark:text-gray-400 font-bold uppercase text-xs tracking-wider mb-1">Orders Completed</div>
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">{stats.selectedDateOrders}</div>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                        <div className="text-slate-500 dark:text-gray-400 font-bold uppercase text-xs tracking-wider mb-1">Avg. Order Value</div>
                        <div className="text-2xl font-bold text-slate-900 dark:text-white">
                             ₱{stats.selectedDateOrders > 0 ? (stats.selectedDateRevenue / stats.selectedDateOrders).toFixed(2) : '0.00'}
                        </div>
                    </div>
                </div>
            </div>
             {/* ... Graphs etc ... */}
             <div className="bg-white dark:bg-slate-800 p-4 md:p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-md overflow-x-auto">
                 <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white mb-6">Revenue Trend (Last 7 Days)</h3>
                 <div className="h-64 flex items-end justify-between gap-2 md:gap-4 min-w-[300px]">
                     {stats.graphData.map((data, idx) => {
                         const maxVal = Math.max(...stats.graphData.map(d => d.value), 1);
                         const heightPercent = (data.value / maxVal) * 100;
                         return (
                             <div key={idx} className="flex flex-col items-center flex-1 h-full justify-end group">
                                 <div className="relative w-full flex items-end justify-center h-[85%]">
                                     <div 
                                       className="w-full max-w-[40px] bg-amber-500/80 dark:bg-amber-600/80 rounded-t-sm hover:bg-amber-500 hover:scale-105 transition-all duration-300 relative group-hover:shadow-lg"
                                       style={{ height: `${heightPercent}%` }}
                                     >
                                         <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10 pointer-events-none">
                                             ₱{data.value.toLocaleString()}
                                         </div>
                                     </div>
                                 </div>
                                 <div className="h-[15%] flex items-center justify-center pt-2">
                                     <span className="text-[10px] md:text-xs text-slate-500 dark:text-gray-400 font-bold">{data.label}</span>
                                 </div>
                             </div>
                         );
                     })}
                 </div>
            </div>
        </div>
      )}

      {/* ... Orders Tab ... */}
      {activeTab === 'orders' && (
        <div className="space-y-4 print:hidden animate-fade-in-up">
          <h3 className="text-xl text-slate-700 dark:text-gray-300 mb-4 font-bold flex items-center gap-2">
            <i className="fas fa-list-ul"></i> Live Orders
          </h3>
          {liveOrders.length === 0 && <p className="text-gray-500 italic p-4 text-center border border-dashed border-slate-300 rounded">No active orders right now.</p>}
          
          {/* MOBILE LIST VIEW (Accordion) */}
          <div className="md:hidden space-y-3">
             {liveOrders.map(order => (
                <div key={order.id} className="bg-white dark:bg-slate-800 rounded-lg shadow border border-slate-200 dark:border-slate-700 overflow-hidden animate-scale-in">
                   {/* Header - Click to Expand */}
                   <div 
                     onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                     className="p-4 flex items-center justify-between cursor-pointer active:bg-slate-50 dark:active:bg-slate-700"
                   >
                      <div className="flex flex-col gap-1">
                         <div className="flex items-center gap-2">
                            <span className="font-bold text-amber-600 dark:text-amber-500">#{order.id.slice(-4)}</span>
                            <span className="text-xs text-slate-400 dark:text-gray-500">{new Date(order.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                         </div>
                         <span className="text-sm font-medium text-slate-800 dark:text-white">{order.customerName}</span>
                      </div>
                      <div className="flex items-center gap-3">
                         <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            order.status === 'ready' ? 'bg-green-100 text-green-700' :
                            order.status === 'preparing' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-700'
                         }`}>
                           {order.status}
                         </span>
                         <i className={`fas fa-chevron-down transition-transform duration-300 text-slate-400 ${expandedOrderId === order.id ? 'rotate-180' : ''}`}></i>
                      </div>
                   </div>
                   
                   {/* Expanded Content */}
                   {expandedOrderId === order.id && (
                     <div className="bg-slate-50 dark:bg-slate-900/50 p-4 border-t border-slate-100 dark:border-slate-700 animate-fade-in-up">
                        <div className="space-y-2 mb-4 bg-white dark:bg-slate-800 p-3 rounded border border-slate-200 dark:border-slate-700">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm text-slate-700 dark:text-gray-300">
                              <span>{item.quantity}x {item.name}</span>
                              {item.note && <span className="text-xs italic text-slate-500">{item.note}</span>}
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between items-center font-bold text-slate-900 dark:text-white mb-4 pt-2">
                           <span>Total</span>
                           <span className="text-lg">₱{order.total.toFixed(2)}</span>
                        </div>
                        
                        {/* Actions */}
                        <div className="grid grid-cols-2 gap-3">
                           <button 
                             onClick={(e) => { e.stopPropagation(); handlePrintOrder(order); }}
                             className="flex items-center justify-center gap-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-white py-3 rounded text-sm font-bold transition"
                           >
                             <i className="fas fa-print"></i> Print
                           </button>
                           <div className="relative">
                              <select 
                                value={order.status} 
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => handleStatusUpdate(order.id, e.target.value as any)}
                                className="w-full bg-amber-600 text-white py-3 px-2 rounded text-sm font-bold outline-none appearance-none text-center"
                              >
                                <option className="text-black" value="pending">Pending</option>
                                <option className="text-black" value="preparing">Preparing</option>
                                <option className="text-black" value="ready">Ready</option>
                                <option className="text-black" value="completed">Complete</option>
                                <option className="text-black" value="cancelled">Cancel</option>
                              </select>
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-white">
                                <i className="fas fa-caret-down"></i>
                              </div>
                           </div>
                        </div>
                     </div>
                   )}
                </div>
             ))}
          </div>

          {/* DESKTOP CARD VIEW */}
          <div className="hidden md:flex flex-col gap-4">
            {liveOrders.map(order => (
              <div key={order.id} className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row justify-between gap-4 border-l-4 border-l-amber-500 shadow-lg transition-colors duration-300 animate-scale-in">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-amber-600 dark:text-amber-400 font-bold text-xl">#{order.id.slice(-5)}</span>
                    <span className="text-slate-500 dark:text-gray-400 text-sm">by {order.customerName}</span>
                    <span className="text-slate-400 dark:text-gray-500 text-xs bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded">{new Date(order.createdAt).toLocaleTimeString()}</span>
                    {order.isRedemption && <span className="bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 text-xs px-2 py-0.5 rounded animate-pulse">REDEMPTION</span>}
                  </div>
                  <div className="space-y-2 mt-3 bg-slate-50 dark:bg-slate-900/50 p-3 rounded">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="text-slate-700 dark:text-gray-200 flex justify-between">
                        <div className="font-medium">{item.quantity}x {item.name}</div>
                        {item.note && <div className="text-xs text-slate-500 dark:text-gray-400 italic">"{item.note}"</div>}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 font-bold text-slate-900 dark:text-white text-lg">Total: ₱{order.total.toFixed(2)}</div>
                </div>
                <div className="flex flex-col gap-2 justify-center min-w-[200px] border-t md:border-t-0 md:border-l border-slate-200 dark:border-slate-700 md:pl-6 pt-4 md:pt-0">
                  <label className="text-xs text-slate-500 uppercase font-bold">Update Status</label>
                  <select 
                    value={order.status} 
                    onChange={(e) => handleStatusUpdate(order.id, e.target.value as Order['status'])}
                    className={`bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white rounded px-4 py-3 text-sm outline-none focus:border-amber-500 transition font-bold
                      ${order.status === 'pending' ? 'border-yellow-500 text-yellow-600 dark:text-yellow-500' : ''}
                      ${order.status === 'ready' ? 'border-green-500 text-green-600 dark:text-green-500' : ''}
                    `}
                  >
                    <option value="pending">Pending</option>
                    <option value="preparing">Preparing</option>
                    <option value="ready">Ready</option>
                    <option value="completed">Completed (Pick up/Delivered)</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  
                  {/* Print Button */}
                  <button 
                    onClick={() => handlePrintOrder(order)}
                    className="mt-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-white py-2 rounded font-bold text-sm flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-print"></i> Print Receipt
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ... History Tab ... */}
      {activeTab === 'history' && (
         <div className="space-y-4 print:hidden animate-fade-in-up">
            <h3 className="text-xl text-slate-700 dark:text-gray-300 mb-4">Order History</h3>
            <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden transition-colors duration-300 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="bg-slate-100 dark:bg-slate-900">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {pastOrders.map(order => (
                    <tr key={order.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-gray-300 font-mono">#{order.id.slice(-5)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 dark:text-gray-300">{new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-800 dark:text-white">{order.customerName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-600 dark:text-amber-500 font-bold">
                        {order.isRedemption ? 'Redemption' : `₱${order.total.toFixed(2)}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${order.status === 'completed' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300'}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                         <button onClick={() => handlePrintOrder(order)} className="text-slate-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400">
                           <i className="fas fa-print"></i>
                         </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
         </div>
       )}

      {/* ... Products Tab ... */}
      {activeTab === 'products' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 print:hidden animate-fade-in-up">
           {/* Add Product Form */}
           <div className="lg:col-span-1">
             <div className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 sticky top-24 transition-colors duration-300 shadow-md">
               <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Add New Product</h3>
               <form onSubmit={handleAddProduct} className="space-y-4">
                 <input className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" placeholder="Name" value={newProduct.name} onChange={e => setNewProduct({...newProduct, name: e.target.value})} />
                 <textarea className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" placeholder="Description" value={newProduct.description} onChange={e => setNewProduct({...newProduct, description: e.target.value})} />
                 <div className="flex gap-2">
                   <input className="w-1/2 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" type="number" step="0.01" placeholder="Price (₱)" value={newProduct.price || ''} onChange={e => setNewProduct({...newProduct, price: parseFloat(e.target.value)})} />
                   <input className="w-1/2 bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" type="number" placeholder="Points Earned" value={newProduct.pointsEarned || ''} onChange={e => setNewProduct({...newProduct, pointsEarned: parseInt(e.target.value)})} />
                 </div>
                 
                 {/* Redeemable Options */}
                 <div className="bg-slate-50 dark:bg-slate-900 p-3 rounded border border-slate-200 dark:border-slate-700">
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                       <input 
                         type="checkbox" 
                         checked={newProduct.isRedeemable || false}
                         onChange={e => setNewProduct({...newProduct, isRedeemable: e.target.checked})}
                         className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                       />
                       <span className="text-sm font-bold text-slate-700 dark:text-gray-300">Available as Reward?</span>
                    </label>
                    {newProduct.isRedeemable && (
                        <input 
                          className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white text-sm" 
                          type="number" 
                          placeholder="Point Cost to Redeem" 
                          value={newProduct.pointCost || ''} 
                          onChange={e => setNewProduct({...newProduct, pointCost: parseInt(e.target.value)})} 
                        />
                    )}
                 </div>

                 <div>
                    <label className="block text-sm text-slate-500 dark:text-gray-400 mb-1">Main Image</label>
                    <input type="file" accept="image/*" onChange={e => handleImageChange(e, true)} className="w-full text-sm text-slate-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-slate-200 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-white hover:file:bg-slate-300 dark:hover:file:bg-slate-600"/>
                 </div>
                 {newProduct.imageUrl && (
                    <img src={newProduct.imageUrl} alt="Preview" className="w-full h-32 object-cover rounded border border-slate-300 dark:border-slate-600 mb-2" />
                 )}

                 <div>
                    <label className="block text-sm text-slate-500 dark:text-gray-400 mb-1">Additional Images</label>
                    <input type="file" accept="image/*" onChange={e => handleImageChange(e, false)} className="w-full text-sm text-slate-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-slate-200 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-white hover:file:bg-slate-300 dark:hover:file:bg-slate-600"/>
                    <div className="flex gap-2 mt-2 overflow-x-auto">
                       {newProduct.additionalImages?.map((img, i) => (
                         <img key={i} src={img} className="w-16 h-16 object-cover rounded border border-slate-300 dark:border-slate-600" alt="" />
                       ))}
                    </div>
                 </div>

                 <select className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" value={newProduct.category} onChange={e => setNewProduct({...newProduct, category: e.target.value as any})}>
                   <option value="coffee">Coffee</option>
                   <option value="tea">Tea</option>
                   <option value="pastry">Pastry</option>
                   <option value="specialty">Specialty</option>
                 </select>
                 <button className="w-full bg-amber-600 hover:bg-amber-500 text-white py-2 rounded font-bold">Add Item</button>
               </form>
             </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
             {products.map(p => (
               <div key={p.id} className={`bg-white dark:bg-slate-800 p-4 rounded-lg border ${p.available ? 'border-slate-200 dark:border-slate-700' : 'border-red-200 dark:border-red-900 opacity-75'} flex gap-4 relative shadow-sm transition-colors duration-300`}>
                 <img src={p.imageUrl} alt={p.name} className={`w-20 h-20 object-cover rounded ${!p.available && 'grayscale'}`} />
                 <div className="flex-1">
                   <h4 className="text-slate-900 dark:text-white font-bold">{p.name}</h4>
                   <p className="text-amber-600 dark:text-amber-500">₱{p.price.toFixed(2)} <span className="text-xs text-slate-500 dark:text-gray-500 ml-1">({p.pointsEarned || Math.floor(p.price)} pts)</span></p>
                   {p.isRedeemable && (
                       <span className="inline-block bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 text-xs px-2 py-0.5 rounded font-bold mt-1">
                           Reward: {p.pointCost} pts
                       </span>
                   )}
                   {p.additionalImages && p.additionalImages.length > 0 && <span className="text-xs text-slate-400 dark:text-gray-400 block mt-1">{p.additionalImages.length + 1} images</span>}
                   {!p.available && <span className="text-red-500 text-xs font-bold uppercase block mt-1">Unavailable</span>}
                   <div className="flex gap-2 mt-2">
                       <button 
                         onClick={() => handleToggleAvailability(p)} 
                         className={`text-xs px-2 py-1 rounded font-bold tracking-wider ${p.available ? 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-gray-300 hover:bg-slate-300 dark:hover:bg-slate-600' : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800'}`}
                       >
                         {p.available ? 'Mark Sold Out' : 'Mark Available'}
                       </button>
                       <button onClick={() => handleDeleteProduct(p.id)} className="text-red-500 dark:text-red-400 text-xs hover:text-red-700 dark:hover:text-red-300 uppercase font-bold tracking-wider ml-auto">Delete</button>
                   </div>
                 </div>
               </div>
             ))}
          </div>
        </div>
      )}

      {/* ... Settings Tab ... */}
      {activeTab === 'settings' && (
        <div className="bg-white dark:bg-slate-800 p-8 rounded-lg border border-slate-200 dark:border-slate-700 max-w-2xl mx-auto shadow-lg transition-colors duration-300 print:hidden animate-fade-in-up">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">Store Branding & Location</h3>
          <form onSubmit={handleSettingsUpdate} className="space-y-8">
             <div>
               <label className="block text-slate-600 dark:text-gray-300 font-medium mb-2">Store Logo</label>
               <div className="flex items-center gap-6">
                 {settings.logoUrl ? (
                   <img src={settings.logoUrl} className="w-24 h-24 rounded-full border-2 border-amber-500 object-cover" alt="Logo" />
                 ) : (
                   <div className="w-24 h-24 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 border-dashed border-slate-400 dark:border-slate-500">
                     <span className="text-slate-500 dark:text-gray-500 text-xs">No Logo</span>
                   </div>
                 )}
                 <input type="file" accept="image/*" onChange={e => handleSettingImage(e, 'logo')} className="text-sm text-slate-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-slate-200 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-white hover:file:bg-slate-300 dark:hover:file:bg-slate-600" />
               </div>
             </div>
             {/* ... */}
             <div>
               <label className="block text-slate-600 dark:text-gray-300 font-medium mb-2">Cover Photo</label>
               <div className="space-y-3">
                 {settings.coverPhotoUrl ? (
                   <img src={settings.coverPhotoUrl} className="w-full h-48 rounded-lg object-cover border border-slate-300 dark:border-slate-600" alt="Cover" />
                 ) : (
                   <div className="w-full h-48 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center border-2 border-dashed border-slate-400 dark:border-slate-500">
                     <span className="text-slate-500 dark:text-gray-500">No Cover Photo</span>
                   </div>
                 )}
                 <input type="file" accept="image/*" onChange={e => handleSettingImage(e, 'cover')} className="text-sm text-slate-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-slate-200 dark:file:bg-slate-700 file:text-slate-700 dark:file:text-white hover:file:bg-slate-300 dark:hover:file:bg-slate-600" />
               </div>
             </div>

             <div>
               <label className="block text-slate-600 dark:text-gray-300 font-medium mb-2">Intro Subtitle</label>
               <textarea 
                 value={settings.introSubtitle || ''} 
                 onChange={e => setSettings({...settings, introSubtitle: e.target.value})}
                 className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                 rows={2}
                 placeholder="Experience the celestial taste of artisanal coffee under the moonlight."
               />
             </div>
             
             <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Google Maps & Social</h4>
                <div className="space-y-4">
                  {/* ... inputs ... */}
                  <div>
                    <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Google Maps Link</label>
                    <input 
                      type="text"
                      value={settings.googleMapsUrl || ''}
                      onChange={e => setSettings({...settings, googleMapsUrl: e.target.value})}
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                      placeholder="e.g., https://goo.gl/maps/..."
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Google Maps Embed URL</label>
                    <input 
                      type="text"
                      value={settings.googleMapsEmbedUrl || ''}
                      onChange={e => setSettings({...settings, googleMapsEmbedUrl: e.target.value})}
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                      placeholder="e.g., https://www.google.com/maps/embed?pb=..."
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Facebook Page URL</label>
                    <input 
                      type="text"
                      value={settings.facebookUrl || ''}
                      onChange={e => setSettings({...settings, facebookUrl: e.target.value})}
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                      placeholder="e.g., https://facebook.com/8MoonCafe"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Facebook Messenger URL</label>
                    <input 
                      type="text"
                      value={settings.facebookMessageUrl || ''}
                      onChange={e => setSettings({...settings, facebookMessageUrl: e.target.value})}
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                      placeholder="e.g., https://m.me/1000..."
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Rating</label>
                      <input 
                        type="number"
                        step="0.1"
                        max="5"
                        value={settings.googleRating || ''}
                        onChange={e => setSettings({...settings, googleRating: parseFloat(e.target.value)})}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                        placeholder="4.8"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Reviews</label>
                      <input 
                        type="number"
                        value={settings.googleReviewCount || ''}
                        onChange={e => setSettings({...settings, googleReviewCount: parseInt(e.target.value)})}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                        placeholder="120"
                      />
                    </div>
                  </div>
                </div>
             </div>

             <div>
               <label className="block text-slate-600 dark:text-gray-300 font-medium mb-2">Footer Text</label>
               <textarea 
                 value={settings.footerText || ''} 
                 onChange={e => setSettings({...settings, footerText: e.target.value})}
                 className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                 rows={2}
                 placeholder="© 2024 8 Moon Cafe. Brewed with Stardust & Code."
               />
             </div>
             
             <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <button className="w-full bg-amber-600 hover:bg-amber-500 text-white py-3 rounded-md font-bold text-lg">Save Settings</button>
             </div>
          </form>
        </div>
      )}

      {/* ... Customers Tab ... */}
      {activeTab === 'customers' && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden shadow-md transition-colors duration-300 print:hidden animate-fade-in-up overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead className="bg-slate-100 dark:bg-slate-900">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Points</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-gray-400 uppercase tracking-wider">Bio</th>
                    </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                    {customers.map((c) => (
                        <tr key={c.uid}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {c.photoURL ? (
                                  <img className="h-10 w-10 rounded-full object-cover mr-3 border border-slate-200 dark:border-slate-600" src={c.photoURL} alt="" />
                                ) : (
                                  <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center mr-3 text-slate-700 dark:text-white font-bold">
                                    {c.displayName?.[0] || '?'}
                                  </div>
                                )}
                                <div className="text-sm font-medium text-slate-900 dark:text-white">{c.displayName}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-gray-400">{c.email}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-amber-600 dark:text-amber-500 font-bold">{c.points}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-gray-400 text-sm truncate max-w-xs">{c.bio || '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      )}
      
      {/* ... NEW RECEIPT DASHBOARD ... */}
      {activeTab === 'receipt' && (
        <div className="flex flex-col lg:flex-row gap-8 animate-fade-in-up print:hidden">
          {/* Editor Side */}
          <div className="lg:w-1/2 bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-md">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Receipt Template Editor</h3>
            <p className="text-sm text-slate-500 dark:text-gray-400 mb-6">Customize how your printed receipts look. Changes here affect all future prints.</p>
            
            <form onSubmit={handleSettingsUpdate} className="space-y-4">
              <div>
                <label className="block text-slate-600 dark:text-gray-300 font-medium mb-1">Header Title</label>
                <input 
                  type="text"
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                  placeholder="e.g., TAX INVOICE"
                  value={settings.receiptHeader || ''}
                  onChange={e => setSettings({...settings, receiptHeader: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-slate-600 dark:text-gray-300 font-medium mb-1">Store Address</label>
                <textarea 
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                  placeholder="123 Galaxy St, Moon Base Alpha"
                  rows={2}
                  value={settings.receiptAddress || ''}
                  onChange={e => setSettings({...settings, receiptAddress: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-slate-600 dark:text-gray-300 font-medium mb-1">Phone Number</label>
                    <input 
                      type="text"
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                      placeholder="e.g., 777-7777"
                      value={settings.receiptPhone || ''}
                      onChange={e => setSettings({...settings, receiptPhone: e.target.value})}
                    />
                </div>
                <div>
                    <label className="block text-slate-600 dark:text-gray-300 font-medium mb-1">Tax Rate (%)</label>
                    <input 
                      type="number"
                      step="0.1"
                      className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                      placeholder="e.g., 12"
                      value={settings.receiptTaxRate || ''}
                      onChange={e => setSettings({...settings, receiptTaxRate: parseFloat(e.target.value)})}
                    />
                </div>
              </div>

              <div>
                <label className="block text-slate-600 dark:text-gray-300 font-medium mb-1">Footer Message</label>
                <textarea 
                  className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                  placeholder="THANK YOU! PLEASE COME AGAIN."
                  value={settings.receiptFooterMessage || ''}
                  onChange={e => setSettings({...settings, receiptFooterMessage: e.target.value})}
                />
              </div>

              <div className="flex items-center gap-2 pt-2">
                 <input 
                   type="checkbox"
                   id="showBarcode"
                   checked={settings.receiptShowBarcode !== false}
                   onChange={e => setSettings({...settings, receiptShowBarcode: e.target.checked})}
                   className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                 />
                 <label htmlFor="showBarcode" className="text-slate-700 dark:text-gray-300 font-medium">Show Barcode at bottom</label>
              </div>

              <div className="pt-4">
                <button className="w-full bg-amber-600 hover:bg-amber-500 text-white py-3 rounded-md font-bold transition shadow-md">
                   Save Template
                </button>
              </div>
            </form>
          </div>

          {/* Preview Side */}
          <div className="lg:w-1/2 flex flex-col items-center">
             <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Live Preview</h3>
             <div className="bg-gray-200 dark:bg-gray-700 p-8 rounded-xl shadow-inner w-full flex justify-center items-start min-h-[500px] overflow-auto">
                 <ReceiptTemplate 
                   order={{
                       id: 'ORD-12345678',
                       userId: 'user123',
                       customerName: 'Guest Customer',
                       items: [
                           { ...products[0] || { id: '1', name: 'Cappuccino', price: 120, description: '', category: 'coffee', available: true, imageUrl: '' }, quantity: 1, available: true, imageUrl: '', description: '', category: 'coffee' },
                           { ...products[1] || { id: '2', name: 'Croissant', price: 95, description: '', category: 'pastry', available: true, imageUrl: '' }, quantity: 2, available: true, imageUrl: '', description: '', category: 'pastry' }
                       ],
                       total: (products[0]?.price || 120) + ((products[1]?.price || 95) * 2),
                       status: 'completed',
                       createdAt: Date.now()
                   }}
                   settings={settings}
                 />
             </div>
             <p className="text-xs text-slate-500 mt-4 text-center">Preview uses dummy data. Actual print will use real order details.</p>
          </div>
        </div>
      )}

      {/* Messages Tab - Optimized for Mobile & Scroll */}
      {activeTab === 'messages' && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 h-[calc(100vh-200px)] md:h-[600px] min-h-[400px] flex overflow-hidden shadow-lg transition-colors duration-300 print:hidden animate-fade-in-up relative">
          
          {/* Chat List - Hidden on mobile if chat is selected */}
          <div className={`${selectedChatId ? 'hidden md:block' : 'block'} w-full md:w-1/3 border-r border-slate-200 dark:border-slate-700 overflow-y-auto bg-slate-50 dark:bg-slate-900/50`}>
             {allChats.length === 0 && <p className="p-4 text-slate-500 text-sm">No messages yet.</p>}
             {allChats.map(chat => (
               <div 
                 key={chat.id} 
                 onClick={() => setSelectedChatId(chat.id)}
                 className={`p-4 border-b border-slate-200 dark:border-slate-700 cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-700/50 transition ${selectedChatId === chat.id ? 'bg-slate-200 dark:bg-slate-700 border-l-4 border-l-amber-500' : ''}`}
               >
                 <div className="flex justify-between items-start mb-1">
                    <span className={`font-bold ${chat.unreadByOwner ? 'text-slate-900 dark:text-white' : 'text-slate-600 dark:text-gray-300'}`}>{chat.customerName}</span>
                    {chat.unreadByOwner && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>}
                 </div>
                 <p className="text-xs text-slate-500 dark:text-gray-500 truncate">{chat.messages[chat.messages.length-1]?.text}</p>
                 <p className="text-[10px] text-slate-400 dark:text-gray-600 mt-1">{new Date(chat.lastUpdated).toLocaleTimeString()}</p>
               </div>
             ))}
          </div>

          {/* Chat Content - Hidden on mobile if NO chat is selected */}
          <div className={`${!selectedChatId ? 'hidden md:flex' : 'flex'} w-full md:w-2/3 flex-col bg-white dark:bg-slate-800 absolute inset-0 md:static z-10`}>
             {activeChatMessages ? (
               <>
                 <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 flex justify-between items-center flex-shrink-0">
                    <div className="flex items-center">
                        <button 
                            onClick={() => setSelectedChatId(null)} 
                            className="md:hidden mr-3 text-slate-500 hover:text-slate-800 dark:text-gray-400 dark:hover:text-white"
                        >
                            <i className="fas fa-arrow-left"></i>
                        </button>
                        <h3 className="font-bold text-slate-900 dark:text-white">{activeChatMessages.customerName}</h3>
                    </div>
                 </div>
                 <div className="flex-1 overflow-y-auto p-4 space-y-3 scroll-smooth min-h-0">
                    {activeChatMessages.messages.length > 500 && (
                        <div className="text-center text-xs text-slate-400 p-2 italic">History truncated for performance.</div>
                    )}
                    {activeChatMessages.messages.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'owner' ? 'justify-end' : 'justify-start'}`}>
                         <div className={`max-w-[85%] rounded-lg px-4 py-2 text-sm whitespace-pre-wrap break-words ${msg.role === 'owner' ? 'bg-amber-600 text-white rounded-br-none' : 'bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-gray-200 rounded-bl-none border border-slate-200 dark:border-slate-600'}`}>
                            {msg.text}
                         </div>
                      </div>
                    ))}
                    <div ref={chatScrollRef}></div>
                 </div>
                 <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-900 flex gap-2 flex-shrink-0">
                    <input 
                      className="flex-1 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white" 
                      placeholder="Type a reply..."
                      value={adminReply}
                      onChange={e => setAdminReply(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSendAdminReply()}
                    />
                    <button onClick={handleSendAdminReply} className="bg-amber-600 hover:bg-amber-500 text-white px-4 rounded">Send</button>
                 </div>
               </>
             ) : (
               <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-gray-500 p-8 text-center">
                 <i className="fas fa-comments text-4xl mb-2 opacity-20"></i>
                 <p>Select a conversation to view details</p>
               </div>
             )}
          </div>
        </div>
      )}
    </div>
  );
};

// ... CustomerOrders, Login ...
const CustomerOrders = ({ user }: { user: User }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const { addToast, confirmAction } = useContext(AppContext);

  useEffect(() => {
    const unsub = subscribeToOrders(setOrders, user.uid);
    return () => unsub();
  }, [user]);

  const handleCancel = async (orderId: string) => {
    confirmAction("Cancel Order", "Are you sure you want to cancel this order?", async () => {
        await updateOrderStatus(orderId, 'cancelled');
        addToast("Order cancelled", "info");
    });
  };

  const canCancel = (order: Order) => {
    const now = Date.now();
    const timeDiff = now - order.createdAt;
    return order.status === 'pending' && timeDiff < 300000;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
       <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8">My Orders</h2>
       {orders.length === 0 ? (
         <p className="text-slate-500 dark:text-gray-400">You haven't ordered any stardust yet.</p>
       ) : (
         <div className="space-y-4">
           {orders.map(order => (
             <div key={order.id} className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700 shadow-lg transition-colors duration-300">
               <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-4">
                 <div>
                   <span className="text-slate-500 dark:text-gray-400 text-sm">{new Date(order.createdAt).toLocaleDateString()} at {new Date(order.createdAt).toLocaleTimeString()}</span>
                   <h3 className="text-amber-600 dark:text-amber-500 font-bold text-lg mt-1">
                      {order.isRedemption ? 'Redeemed Reward' : `Total: ₱${order.total.toFixed(2)}`}
                   </h3>
                   {order.pointsAwarded && (
                     <span className="text-xs text-green-600 dark:text-green-400 block mt-1"><i className="fas fa-check-circle"></i> Points Earned</span>
                   )}
                 </div>
                 <div className="flex items-center gap-2">
                   <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase
                     ${order.status === 'completed' ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300' : 
                       order.status === 'cancelled' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-300' : 
                       'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300'}
                   `}>
                     {order.status}
                   </span>
                   {canCancel(order) && (
                     <button 
                       onClick={() => handleCancel(order.id)}
                       className="bg-red-600 hover:bg-red-500 text-white text-xs px-3 py-1 rounded-full transition"
                     >
                       Cancel Order
                     </button>
                   )}
                 </div>
               </div>
               {/* Order Items */}
               <div className="space-y-3 bg-slate-50 dark:bg-slate-900/50 p-4 rounded border border-slate-100 dark:border-slate-700/50">
                 {order.items.map((item, i) => (
                   <div key={i} className="flex justify-between text-slate-700 dark:text-gray-300 border-b border-slate-200 dark:border-slate-700/50 pb-2 last:border-0 last:pb-0">
                     <div className="flex flex-col">
                       <span>{item.quantity}x {item.name}</span>
                       {item.note && <span className="text-xs text-slate-500 dark:text-gray-500 italic">Note: {item.note}</span>}
                     </div>
                     <span>{item.price === 0 ? 'FREE' : `₱${(item.price * item.quantity).toFixed(2)}`}</span>
                   </div>
                 ))}
               </div>
               {canCancel(order) && <p className="text-[10px] text-slate-400 dark:text-gray-500 mt-2 text-right">You can cancel within 5 minutes of ordering.</p>}
             </div>
           ))}
         </div>
       )}
    </div>
  );
};

// ... Login component remains similar ...
const Login = () => {
    // ... logic same ...
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (isRegistering) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await createUserProfile({
          uid: userCredential.user.uid,
          email: userCredential.user.email!,
          role: 'customer',
          points: 0,
          bio: 'New explorer',
          photoURL: ''
        });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-[80vh] bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 p-8 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-md transition-colors duration-300">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-6 text-center">{isRegistering ? 'Join the Galaxy' : 'Welcome Back'}</h2>
        {error && <div className="bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-300 p-3 rounded mb-4 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Email</label>
            <input 
              type="email" 
              required
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white focus:border-amber-500 outline-none" 
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-slate-500 dark:text-gray-400 text-sm mb-1">Password</label>
            <input 
              type="password" 
              required
              className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white focus:border-amber-500 outline-none" 
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          <button type="submit" className="w-full bg-amber-600 hover:bg-amber-500 text-white py-2 rounded font-medium transition mt-4">
            {isRegistering ? 'Sign Up' : 'Login'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button 
            onClick={() => setIsRegistering(!isRegistering)}
            className="text-amber-600 hover:text-amber-500 dark:text-amber-400 dark:hover:text-amber-300 text-sm"
          >
            {isRegistering ? 'Already have an account? Login' : 'Need an account? Sign Up'}
          </button>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [storeSettings, setStoreSettings] = useState<StoreSettings>({});
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // Notification State
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [modalConfig, setModalConfig] = useState<{isOpen: boolean, title: string, message: string, onConfirm: () => void, onCancel: () => void}>({
      isOpen: false, title: '', message: '', onConfirm: () => {}, onCancel: () => {}
  });

  const addToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
      const id = Date.now();
      setToasts(prev => [...prev, { id, message, type }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  const confirmAction = (title: string, message: string, onConfirm: () => void) => {
      setModalConfig({
          isOpen: true,
          title,
          message,
          onConfirm: () => {
              onConfirm();
              setModalConfig(prev => ({ ...prev, isOpen: false }));
          },
          onCancel: () => setModalConfig(prev => ({ ...prev, isOpen: false }))
      });
  };

  // ... Initial Data Load ...
  useEffect(() => {
    getProducts().then(setProducts);
    getStoreSettings().then(setStoreSettings);

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
         const unsubProfile = onSnapshot(doc(db, "users", currentUser.uid), (docSnap) => {
             if (docSnap.exists()) {
                 setProfile(docSnap.data() as UserProfile);
             } else {
                 const newProfile: UserProfile = { 
                   uid: currentUser.uid, 
                   email: currentUser.email!, 
                   role: 'customer',
                   points: 0,
                   bio: 'New explorer',
                   photoURL: ''
                 };
                 setDoc(doc(db, "users", currentUser.uid), newProfile);
                 setProfile(newProfile);
             }
         });
         setLoading(false);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  const handleLogout = async () => {
    await signOut(auth);
    setCart([]); 
    window.location.reload(); 
  };

  const handleRedeem = (product: Product) => {
    if (!user || !profile) {
      addToast("Please login to redeem rewards.", "error");
      return;
    }
    const cost = product.pointCost || 999999;
    confirmAction("Redeem Reward", `Redeem ${product.name} for ${cost} points?`, async () => {
        if (profile.points >= cost) {
          try {
            await redeemProduct(profile.uid, profile.displayName || 'Customer', product, cost);
            addToast("Reward redeemed! Check your orders.", "success");
          } catch (err) {
            console.error(err);
            addToast("Failed to redeem reward.", "error");
          }
        } else {
          addToast(`Not enough points. You need ${cost - profile.points} more.`, "error");
        }
    });
  };

  const addToCart = (product: Product, note?: string) => {
    setCart(prev => {
      const existing = prev.find(p => p.id === product.id && p.note === note);
      if (existing) {
        return prev.map(p => (p.id === product.id && p.note === note) ? { ...p, quantity: p.quantity + 1 } : p);
      }
      return [...prev, { ...product, quantity: 1, note }];
    });
    addToast(`Added ${product.name} to cart`, 'success');
  };

  const updateCartQuantity = (id: string, qty: number) => {
    if (qty <= 0) {
      removeFromCart(id);
      return;
    }
    setCart(prev => prev.map(p => p.id === id ? { ...p, quantity: qty } : p));
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(p => p.id !== id));
  };

  const cartTotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  const handleCheckout = async () => {
    if (!user) {
      addToast("Please login to checkout", "error");
      return;
    }
    confirmAction("Place Order", `Confirm total of ₱${cartTotal.toFixed(2)}?`, async () => {
        try {
            const order: Omit<Order, 'id'> = {
                userId: user.uid,
                customerName: profile?.displayName || user.email?.split('@')[0] || 'Guest',
                items: cart,
                total: cartTotal,
                status: 'pending',
                createdAt: Date.now()
            };
            await createOrder(order);
            setCart([]);
            addToast("Order sent successfully!", "success");
        } catch (error) {
            console.error("Order creation failed:", error);
            addToast("Failed to submit order. Please try again.", "error");
        }
    });
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white"><LoadingSpinner /></div>;

  return (
    <AppContext.Provider value={{ addToast, confirmAction }}>
        <HashRouter>
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 font-sans flex flex-col relative pb-20 md:pb-0 transition-colors duration-300 print:bg-white print:text-black">
            <ToastContainer toasts={toasts} />
            <ConfirmationModal 
                isOpen={modalConfig.isOpen} 
                title={modalConfig.title} 
                message={modalConfig.message} 
                onConfirm={modalConfig.onConfirm} 
                onCancel={modalConfig.onCancel} 
            />

            <DesktopNavbar 
            user={user} 
            profile={profile} 
            cartCount={cartCount} 
            onLogout={handleLogout} 
            settings={storeSettings}
            onOpenProfile={() => setIsProfileModalOpen(true)}
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            />
            <MobileHeader 
            user={user} 
            profile={profile} 
            settings={storeSettings}
            onOpenProfile={() => setIsProfileModalOpen(true)}
            isDarkMode={isDarkMode}
            toggleTheme={toggleTheme}
            />
            
            <div className="flex-grow flex flex-col">
            <Routes>
                <Route path="/" element={<Home settings={storeSettings} />} />
                <Route path="/menu" element={<Menu addToCart={addToCart} products={products} user={user} profile={profile} onRedeem={handleRedeem} />} />
                <Route path="/wall" element={<MoonlitWall user={user} profile={profile} />} />
                <Route path="/cart" element={
                <Cart 
                    cart={cart} 
                    updateQuantity={updateCartQuantity} 
                    removeFromCart={removeFromCart} 
                    checkout={handleCheckout}
                    total={cartTotal}
                />
                } />
                <Route path="/login" element={<Login />} />
                <Route path="/orders" element={user ? <CustomerOrders user={user} /> : <Navigate to="/login" />} />
                <Route path="/admin" element={profile?.role === 'owner' ? <AdminDashboard profile={profile} /> : <Navigate to="/" />} />
            </Routes>
            </div>
            
            <MobileBottomNav 
            cartCount={cartCount}
            user={user}
            role={profile?.role}
            />

            {/* Profile Modal */}
            {user && profile && (
            <ProfileModal 
                isOpen={isProfileModalOpen} 
                onClose={() => setIsProfileModalOpen(false)} 
                profile={profile}
                products={products}
                onLogout={handleLogout}
                onRedeem={handleRedeem}
            />
            )}

            {/* Global Floating Chat */}
            <FloatingChat user={user} profile={profile} products={products} />

            {/* Footer hidden on mobile if preferred, or just shown at bottom of scroll */}
            <footer className="bg-white dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800 py-8 text-center text-slate-500 dark:text-gray-500 text-sm mt-auto w-full hidden md:block transition-colors duration-300 print:hidden">
            <p>{storeSettings.footerText || "© 2024 8 Moon Cafe. Brewed with Stardust & Code."}</p>
            </footer>
        </div>
        </HashRouter>
    </AppContext.Provider>
  );
};

const Home = ({ settings }: { settings: StoreSettings }) => {
    // ... same ...
    const renderStars = (rating: number) => {
      const stars = [];
      for (let i = 1; i <= 5; i++) {
        if (rating >= i) {
          stars.push(<i key={i} className="fas fa-star"></i>);
        } else if (rating >= i - 0.5) {
          stars.push(<i key={i} className="fas fa-star-half-alt"></i>);
        } else {
          stars.push(<i key={i} className="far fa-star"></i>);
        }
      }
      return <div className="flex text-amber-500 text-sm">{stars}</div>;
    };
  
    return (
      <div className="flex flex-col flex-grow">
        <div className="relative bg-slate-900 flex-grow flex items-center min-h-[500px]">
          <div className="absolute inset-0 z-0 opacity-40">
            <img 
              src={settings.coverPhotoUrl || "https://images.unsplash.com/photo-1497935586351-b67a49e012bf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"} 
              alt="Coffee Background" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900 dark:to-slate-900 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
            <div className="text-center">
              {settings.logoUrl && (
                <img src={settings.logoUrl} alt="Logo" className="w-24 h-24 mx-auto mb-4 rounded-full border-2 border-amber-500 shadow-lg object-cover" />
              )}
              <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 tracking-tight drop-shadow-lg animate-fade-in-up">
                <span className="block text-amber-500 font-serif mb-2">8 Moon</span>
                <span className="block">Cafe</span>
              </h1>
              <p className="mt-4 max-w-lg mx-auto text-xl text-gray-200 mb-8 drop-shadow-md">
                {settings.introSubtitle || "Experience the celestial taste of artisanal coffee under the moonlight."}
              </p>
              <div className="flex justify-center gap-4 flex-wrap">
                <Link to="/menu" className="px-8 py-3 border border-transparent text-base font-medium rounded-md text-slate-900 bg-amber-500 hover:bg-amber-400 md:py-4 md:text-lg md:px-10 transition shadow-lg shadow-amber-500/20">
                  Order Now
                </Link>
                {/* Facebook Buttons */}
                {settings.facebookUrl && (
                   <a href={settings.facebookUrl} target="_blank" rel="noreferrer" className="px-6 py-3 border border-white/30 text-base font-medium rounded-md text-white bg-[#1877F2]/80 hover:bg-[#1877F2] md:py-4 md:text-lg transition shadow-lg backdrop-blur-sm flex items-center gap-2">
                     <i className="fab fa-facebook"></i> Follow Us
                   </a>
                )}
                {settings.facebookMessageUrl && (
                   <a href={settings.facebookMessageUrl} target="_blank" rel="noreferrer" className="px-6 py-3 border border-white/30 text-base font-medium rounded-md text-white bg-[#0084FF]/80 hover:bg-[#0084FF] md:py-4 md:text-lg transition shadow-lg backdrop-blur-sm flex items-center gap-2">
                     <i className="fab fa-facebook-messenger"></i> Message Us
                   </a>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 py-16 transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900/50 backdrop-blur border border-slate-200 dark:border-slate-700 shadow-md">
                <i className="fas fa-mug-hot text-amber-500 text-4xl mb-4"></i>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Premium Beans</h3>
                <p className="text-slate-600 dark:text-gray-400">Sourced from the finest growers across the galaxy (and Earth).</p>
                </div>
                <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900/50 backdrop-blur border border-slate-200 dark:border-slate-700 shadow-md">
                <i className="fas fa-robot text-amber-500 text-4xl mb-4"></i>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">AI Barista</h3>
                <p className="text-slate-600 dark:text-gray-400">Let Moon Bot suggest your perfect drink based on your mood.</p>
                </div>
                <div className="p-6 rounded-xl bg-slate-50 dark:bg-slate-900/50 backdrop-blur border border-slate-200 dark:border-slate-700 shadow-md">
                <i className="fas fa-moon text-amber-500 text-4xl mb-4"></i>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Moon Points</h3>
                <p className="text-slate-600 dark:text-gray-400">Earn celestial points with every order and redeem for exclusive rewards.</p>
                </div>
            </div>
            </div>
        </div>
        {(settings.googleMapsEmbedUrl || settings.googleMapsUrl || settings.facebookUrl) && (
            <div className="bg-slate-50 dark:bg-slate-900 py-16 border-t border-slate-200 dark:border-slate-800 transition-colors duration-300">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row gap-12 items-center">
                <div className="lg:w-1/2 text-center lg:text-left">
                    <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">Visit Our Galaxy</h2>
                    <p className="text-slate-600 dark:text-gray-400 mb-6">Come taste the stars in person. We are located in the heart of the city, serving moonlight in every cup.</p>
                    
                    {(settings.googleRating || settings.googleReviewCount) && (
                    <div className="mb-6 bg-white dark:bg-slate-800 inline-block p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                        <div className="flex items-center gap-2 mb-1 justify-center lg:justify-start">
                        <span className="text-2xl font-bold text-slate-900 dark:text-white">{settings.googleRating || "4.8"}</span>
                        {renderStars(settings.googleRating || 4.8)}
                        </div>
                        <div className="text-slate-500 dark:text-gray-400 text-sm">{settings.googleReviewCount || 100}+ Google Reviews</div>
                    </div>
                    )}
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start flex-wrap">
                    {settings.googleMapsUrl && (
                        <>
                        <a 
                            href={settings.googleMapsUrl} 
                            target="_blank" 
                            rel="noreferrer"
                            className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-md font-medium transition flex items-center justify-center gap-2"
                        >
                            <i className="fas fa-map-marker-alt"></i> Get Directions
                        </a>
                        <a 
                            href={settings.googleMapsUrl} 
                            target="_blank" 
                            rel="noreferrer"
                            className="bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-600 px-6 py-3 rounded-md font-medium transition flex items-center justify-center gap-2"
                        >
                            <i className="fas fa-star"></i> Rate Us on Google
                        </a>
                        </>
                    )}
                    </div>
                </div>
                
                <div className="lg:w-1/2 w-full">
                    {settings.googleMapsEmbedUrl ? (
                    <div className="rounded-xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-700 h-[300px] md:h-[400px]">
                        <iframe 
                        src={settings.googleMapsEmbedUrl} 
                        width="100%" 
                        height="100%" 
                        style={{ border: 0 }} 
                        allowFullScreen 
                        loading="lazy" 
                        referrerPolicy="no-referrer-when-downgrade"
                        title="Google Maps Location"
                        ></iframe>
                    </div>
                    ) : (
                    <div className="rounded-xl bg-slate-200 dark:bg-slate-800 h-[300px] flex items-center justify-center border border-slate-300 dark:border-slate-700">
                        <div className="text-center text-slate-500">
                        <i className="fas fa-map text-5xl mb-4 block"></i>
                        <p>Map view unavailable</p>
                        </div>
                    </div>
                    )}
                </div>
                </div>
            </div>
            </div>
        )}
      </div>
    );
};

const ProductModal = ({ product, onClose, addToCart }: { product: Product, onClose: () => void, addToCart: (p: Product, note?: string) => void }) => {
    // ... same ...
    const [activeImage, setActiveImage] = useState(product.imageUrl);
    const [note, setNote] = useState('');
    const images = [product.imageUrl, ...(product.additionalImages || [])];
  
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in-up" onClick={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-xl max-w-4xl w-full flex flex-col md:flex-row overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-700 animate-scale-in" onClick={e => e.stopPropagation()}>
          <div className="md:w-1/2 p-4 bg-slate-100 dark:bg-slate-900 flex flex-col">
            <div className="flex-1 flex items-center justify-center overflow-hidden rounded-lg mb-4 bg-black">
              <img src={activeImage} alt={product.name} className="max-w-full max-h-[400px] object-contain" />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {images.map((img, idx) => (
                <button key={idx} onClick={() => setActiveImage(img)} className={`flex-shrink-0 w-16 h-16 rounded border-2 ${activeImage === img ? 'border-amber-500' : 'border-transparent'} overflow-hidden`}>
                  <img src={img} className="w-full h-full object-cover" alt="" />
                </button>
              ))}
            </div>
          </div>
          <div className="md:w-1/2 p-6 flex flex-col">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white">{product.name}</h2>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:text-gray-400 dark:hover:text-white"><i className="fas fa-times text-xl"></i></button>
            </div>
            <p className="text-amber-600 dark:text-amber-500 text-2xl font-bold mb-2">₱{product.price.toFixed(2)}</p>
            <div className="inline-block bg-slate-100 dark:bg-slate-700 text-amber-600 dark:text-amber-200 text-xs px-2 py-1 rounded mb-4 w-max">
              Earns {product.pointsEarned || Math.floor(product.price)} Moon Points
            </div>
            <p className="text-slate-600 dark:text-gray-300 mb-6 leading-relaxed">{product.description}</p>
            
            <div className="mb-6">
              <label className="block text-sm text-slate-500 dark:text-gray-400 mb-1">Special Instructions / Customization</label>
              <textarea 
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white text-sm h-20 placeholder-slate-400 dark:placeholder-gray-600 focus:outline-none focus:border-amber-500"
                placeholder="e.g., Less sugar, extra hot, no cinnamon..."
              />
            </div>
  
            <div className="mt-auto">
              <button 
                onClick={() => { addToCart(product, note); onClose(); }}
                disabled={!product.available}
                className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 dark:disabled:text-gray-500 text-white py-3 rounded-md font-bold text-lg transition shadow-lg"
              >
                {product.available ? 'Add to Cart' : 'Sold Out'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
};

const Menu = ({ 
    addToCart, 
    products,
    user,
    profile,
    onRedeem
  }: { 
    addToCart: (p: Product, note?: string) => void, 
    products: Product[],
    user: User | null,
    profile: UserProfile | null,
    onRedeem: (p: Product) => void
  }) => {
    // ... same ...
    const [activeCategory, setActiveCategory] = useState<string>('all');
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
    const categories = ['all', 'coffee', 'tea', 'pastry', 'specialty', 'rewards'];

    const filteredProducts = activeCategory === 'rewards' 
      ? products.filter(p => p.isRedeemable)
      : activeCategory === 'all'
        ? products
        : products.filter(p => p.category === activeCategory);
  
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {selectedProduct && (
          <ProductModal 
            product={selectedProduct} 
            onClose={() => setSelectedProduct(null)} 
            addToCart={addToCart} 
          />
        )}
  
        <div className="flex flex-col">
          {/* Menu Section */}
          <div className="w-full">
             <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 border-b border-slate-200 dark:border-slate-700 pb-4 gap-4 transition-colors duration-300">
               <h2 className="text-3xl font-bold text-slate-900 dark:text-white">
                   {activeCategory === 'rewards' ? 'Redeem Rewards' : 'Our Menu'}
               </h2>
               <div className="flex flex-wrap gap-2">
                 {categories.map(cat => (
                   <button 
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium capitalize transition ${activeCategory === cat ? 'bg-amber-500 text-white dark:text-slate-900' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-gray-300 hover:bg-slate-300 dark:hover:bg-slate-700'}`}
                   >
                     {cat === 'rewards' ? '🎁 Rewards' : cat}
                   </button>
                 ))}
               </div>
             </div>
  
             {activeCategory === 'rewards' && (
                 <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex justify-between items-center animate-fade-in-up">
                    <div>
                        <h4 className="font-bold text-amber-800 dark:text-amber-200">Moon Points Rewards</h4>
                        <p className="text-sm text-amber-700 dark:text-amber-300">Redeem your earned points for these exclusive items!</p>
                    </div>
                    <div className="text-right">
                        <span className="text-xs text-amber-600 dark:text-amber-400 font-bold uppercase">Your Balance</span>
                        <div className="text-2xl font-bold text-amber-600 dark:text-amber-500">
                             {user && profile ? `${profile.points} pts` : 'Login to view'}
                        </div>
                    </div>
                 </div>
             )}

             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 animate-fade-in-up">
               {filteredProducts.map(product => (
                 <div key={product.id} className={`bg-white dark:bg-slate-800 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col group border ${product.available ? 'border-slate-100 dark:border-slate-700' : 'border-red-100 dark:border-red-900/30'}`}>
                    <div className="relative h-48 overflow-hidden cursor-pointer" onClick={() => setSelectedProduct(product)}>
                      <img src={product.imageUrl} alt={product.name} className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${!product.available && 'grayscale'}`} />
                      {!product.available && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <span className="bg-red-600 text-white px-3 py-1 text-xs font-bold uppercase rounded tracking-wider">Sold Out</span>
                        </div>
                      )}
                      {activeCategory !== 'rewards' && product.pointsEarned && (
                         <div className="absolute top-2 right-2 bg-amber-500/90 text-white text-[10px] px-2 py-1 rounded-full font-bold shadow-sm backdrop-blur-sm">
                           +{product.pointsEarned} Pts
                         </div>
                      )}
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-bold text-slate-900 dark:text-white leading-tight cursor-pointer hover:text-amber-600 dark:hover:text-amber-500 transition" onClick={() => setSelectedProduct(product)}>{product.name}</h3>
                        <span className="text-amber-600 dark:text-amber-500 font-bold">
                            {activeCategory === 'rewards' ? `${product.pointCost} pts` : `₱${product.price.toFixed(0)}`}
                        </span>
                      </div>
                      <p className="text-slate-500 dark:text-gray-400 text-sm mb-4 line-clamp-2 flex-1">{product.description}</p>
                      
                      {activeCategory === 'rewards' ? (
                          <button 
                            onClick={() => onRedeem(product)}
                            disabled={!product.available || !user || (profile ? profile.points < (product.pointCost || 99999) : true)}
                            className="w-full bg-green-600 hover:bg-green-500 disabled:bg-slate-200 dark:disabled:bg-slate-800 disabled:text-slate-400 text-white py-2 rounded-lg font-medium transition flex items-center justify-center gap-2"
                          >
                             {user && profile && profile.points < (product.pointCost || 99999) ? 'Not Enough Points' : 'Redeem Now'}
                          </button>
                      ) : (
                          <button 
                            onClick={() => addToCart(product)}
                            disabled={!product.available}
                            className="w-full bg-slate-900 dark:bg-slate-700 hover:bg-amber-600 dark:hover:bg-amber-600 disabled:bg-slate-200 dark:disabled:bg-slate-800 disabled:text-slate-400 text-white py-2 rounded-lg font-medium transition flex items-center justify-center gap-2 group-hover:bg-amber-600"
                          >
                            <i className="fas fa-plus"></i> Add to Cart
                          </button>
                      )}
                    </div>
                 </div>
               ))}
             </div>
             {filteredProducts.length === 0 && (
               <div className="text-center py-20">
                 <p className="text-slate-500 dark:text-gray-400 text-lg">No items found in this category.</p>
               </div>
             )}
          </div>
        </div>
      </div>
    );
};

const Cart = ({ cart, updateQuantity, removeFromCart, checkout, total }: { cart: CartItem[], updateQuantity: (id: string, qty: number) => void, removeFromCart: (id: string) => void, checkout: () => void, total: number }) => (
    // ... same ...
    <div className="max-w-4xl mx-auto px-4 py-10">
      <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8">Your Cart</h2>
      {cart.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-lg shadow-inner">
          <p className="text-slate-500 dark:text-gray-400 text-xl mb-4">Your cart is empty.</p>
          <Link to="/menu" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-3 rounded-md font-medium inline-block transition">Browse Menu</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item, idx) => (
              <div key={idx} className="bg-white dark:bg-slate-800 p-4 rounded-lg flex items-center gap-4 shadow-sm border border-slate-100 dark:border-slate-700 transition-colors duration-300 animate-scale-in">
                <img src={item.imageUrl} alt={item.name} className="w-20 h-20 object-cover rounded" />
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900 dark:text-white">{item.name}</h3>
                  <p className="text-amber-600 dark:text-amber-500">₱{item.price.toFixed(2)}</p>
                  {item.note && <p className="text-xs text-slate-500 dark:text-gray-400 italic">Note: {item.note}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-600 dark:text-white flex items-center justify-center transition">-</button>
                  <span className="font-bold text-slate-900 dark:text-white w-4 text-center">{item.quantity}</span>
                  <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-600 dark:text-white flex items-center justify-center transition">+</button>
                </div>
                <button onClick={() => removeFromCart(item.id)} className="text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition ml-2">
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            ))}
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 transition-colors duration-300 sticky top-24">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Order Summary</h3>
              <div className="flex justify-between mb-2 text-slate-600 dark:text-gray-300">
                <span>Subtotal</span>
                <span>₱{total.toFixed(2)}</span>
              </div>
              <div className="border-t border-slate-200 dark:border-slate-700 my-4 pt-4 flex justify-between font-bold text-lg text-slate-900 dark:text-white">
                <span>Total</span>
                <span className="text-amber-600 dark:text-amber-500">₱{total.toFixed(2)}</span>
              </div>
              <button onClick={checkout} className="w-full bg-amber-600 hover:bg-amber-500 text-white py-3 rounded-md font-bold shadow-lg transition">Checkout</button>
            </div>
          </div>
        </div>
      )}
    </div>
);

const ProfileModal = ({ 
    isOpen, 
    onClose, 
    profile, 
    products,
    onLogout,
    onRedeem
  }: { 
    isOpen: boolean, 
    onClose: () => void, 
    profile: UserProfile, 
    products: Product[],
    onLogout: () => void,
    onRedeem: (p: Product) => void
  }) => {
    // ... same ...
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({ displayName: profile.displayName || '', bio: profile.bio || '', photoURL: profile.photoURL || '' });
    const { addToast, confirmAction } = useContext(AppContext);
  
    if (!isOpen) return null;
  
    const handleSave = async () => {
      await updateUserProfile(profile.uid, editData);
      setIsEditing(false);
      addToast("Profile updated!", "success");
    };

    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                const compressed = await compressImage(file);
                setEditData(prev => ({...prev, photoURL: compressed}));
            } catch (err) {
                addToast("Failed to process image", "error");
            }
        }
    }

    const handleLogoutClick = () => {
        confirmAction("Sign Out", "Are you sure you want to sign out?", () => {
            onClose();
            onLogout();
        });
    };
  
    const redeemables = products.filter(p => p.isRedeemable);
  
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in-up" onClick={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-700 animate-scale-in" onClick={e => e.stopPropagation()}>
          <div className="bg-slate-100 dark:bg-slate-900 p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Explorer Profile</h2>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:text-gray-400 dark:hover:text-white"><i className="fas fa-times text-xl"></i></button>
          </div>
          
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            <div className="flex flex-col md:flex-row gap-6 mb-8">
               <div className="flex-shrink-0 flex flex-col items-center">
                  <div className="w-24 h-24 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-3xl font-bold text-slate-500 dark:text-white mb-3 overflow-hidden border-2 border-amber-500">
                     {isEditing ? (
                         editData.photoURL ? <img src={editData.photoURL} className="w-full h-full object-cover" alt="Profile"/> : <i className="fas fa-user"></i>
                     ) : (
                         profile.photoURL ? <img src={profile.photoURL} className="w-full h-full object-cover" alt="Profile"/> : (profile.displayName?.[0] || profile.email[0]).toUpperCase()
                     )}
                  </div>
                  {isEditing && (
                      <label className="cursor-pointer bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-xs px-2 py-1 rounded text-slate-700 dark:text-white">
                          Change Photo
                          <input type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} />
                      </label>
                  )}
               </div>
               
               <div className="flex-1">
                  {isEditing ? (
                    <div className="space-y-3">
                      <input 
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                        placeholder="Display Name"
                        value={editData.displayName}
                        onChange={e => setEditData({...editData, displayName: e.target.value})}
                      />
                      <textarea 
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-3 py-2 text-slate-900 dark:text-white"
                        placeholder="Your Bio"
                        rows={2}
                        value={editData.bio}
                        onChange={e => setEditData({...editData, bio: e.target.value})}
                      />
                      <div className="flex gap-2">
                        <button onClick={handleSave} className="bg-amber-600 text-white px-4 py-1 rounded text-sm">Save</button>
                        <button onClick={() => setIsEditing(false)} className="bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-white px-4 py-1 rounded text-sm">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{profile.displayName || 'Anonymous Explorer'}</h3>
                      <p className="text-slate-500 dark:text-gray-400 mb-2">{profile.email}</p>
                      <p className="text-slate-600 dark:text-gray-300 italic mb-4">"{profile.bio || 'Exploring the coffee universe.'}"</p>
                      <div className="flex items-center gap-4">
                         <div className="bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 px-3 py-1 rounded-full text-sm font-bold">
                           <i className="fas fa-star mr-1"></i> {profile.points} Points
                         </div>
                         <button onClick={() => { setEditData({ displayName: profile.displayName || '', bio: profile.bio || '', photoURL: profile.photoURL || '' }); setIsEditing(true); }} className="text-slate-400 hover:text-slate-600 dark:text-gray-500 dark:hover:text-gray-300 text-sm underline">Edit Profile</button>
                      </div>
                      
                      {/* Mobile Logout Option - Hidden on md+ since navbar has logout */}
                      <button 
                        onClick={handleLogoutClick} 
                        className="mt-6 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-bold flex items-center gap-2 md:hidden"
                      >
                        <i className="fas fa-sign-out-alt"></i> Sign Out
                      </button>
                    </div>
                  )}
               </div>
            </div>
  
            <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
               <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Redeem Rewards</h4>
               {redeemables.length === 0 ? (
                 <p className="text-slate-500 text-sm">No rewards available at the moment.</p>
               ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {redeemables.map(item => (
                    <div key={item.id} className={`border p-3 rounded-lg flex items-center gap-3 ${profile.points >= (item.pointCost || 0) ? 'border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-900/20' : 'border-slate-200 dark:border-slate-700 opacity-60'}`}>
                      <img src={item.imageUrl} alt="" className="w-12 h-12 rounded object-cover" />
                      <div className="flex-1">
                        <p className="font-bold text-sm text-slate-900 dark:text-white">{item.name}</p>
                        <p className="text-xs text-slate-500 dark:text-gray-400">{item.pointCost} pts</p>
                      </div>
                      <button 
                        onClick={() => onRedeem(item)}
                        disabled={profile.points < (item.pointCost || 0)}
                        className={`text-xs px-2 py-1 rounded font-bold ${profile.points >= (item.pointCost || 0) ? 'bg-green-600 text-white hover:bg-green-500' : 'bg-slate-300 dark:bg-slate-700 text-slate-500 cursor-not-allowed'}`}
                      >
                        Redeem
                      </button>
                    </div>
                  ))}
                </div>
               )}
            </div>
          </div>
        </div>
      </div>
    );
};

export default App;
